/**
 * Best-effort Supabase dual-write wrapper (ATL-461) + failure observability (ATL-464).
 *
 * During the migration the frontend dual-writes: Firestore is committed FIRST (the source
 * of truth while reads are on Firestore), then the same operation is mirrored to Supabase via
 * the backend. By the time the Supabase call runs, Firestore has already committed — so a
 * Supabase failure must NEVER surface to the user.
 *
 * Failures are expected and benign in normal operation, e.g.:
 *  - the backend/Supabase is briefly down,
 *  - a referenced entity isn't in Supabase yet because another user in the org is still on the
 *    OLD (Firestore-only) app, or the org's baseline hasn't been migrated,
 *  - a transform/validation mismatch.
 *
 * In every case Firestore is already correct. This helper logs + reports the failure and
 * returns null instead of throwing. Callers that use the return value MUST tolerate null
 * (skip any Supabase-derived follow-up; do not block the Firestore-backed flow).
 *
 * DURABILITY (ATL-782): a TRANSIENT failure (timeout, 5xx, network, 401 not-migrated) is
 * additionally persisted to the dual-write outbox (`dualWriteOutbox.ts`) and replayed with
 * backoff on later launches/sales until it lands or is dropped loudly. Deterministic shape
 * defects are never queued. The reconciliation sweep is NOT the safety net here — it is
 * paused (ATL-707) and its return is not a precondition for these writes healing.
 *
 * DESTRUCTIVE OPS ARE NOT FATAL EITHER — decided explicitly (ATL-902), not by default.
 *
 * The question was whether a failed delete/reset should throw to the user, on the reasoning that a
 * delete which half-applies is the worst failure mode this system has: Firestore drops the row,
 * Supabase keeps it, and a later read cutover RESURRECTS it in the customer's books. That reasoning
 * is right about the damage and wrong about the remedy, for three reasons:
 *
 *  1. There is nothing to abort. Firestore committed before this helper was called, so the delete
 *     genuinely succeeded in the store the customer is reading. Throwing reports a failure for an
 *     operation that worked.
 *  2. The obvious human response makes it worse. A cashier told "delete failed" deletes again;
 *     in flows that delete-then-recreate, the retry is what corrupts the books.
 *  3. It is unactionable by the person shown it. The cause is a bundle built against a retired
 *     host, and the fix is an app update — nothing a till operator can do mid-shift.
 *
 * So the containment is placed where it can actually act, and all three parts are required:
 *     • the write is held in the outbox across the device's next OTA rather than being retried to
 *       death against a dead host (`DEAD_BACKEND_RETRY_MS` in dualWriteOutbox.ts),
 *     • the failure is reported as its own alertable class instead of as transient noise (below),
 *     • the read cutover is BLOCKED for any org with such a device, and cannot be cleared by the
 *       device merely updating (`deadBackendRisk` in ota-coverage.util.ts).
 * Revisit only if reads move to Supabase while a bundle can still write one-sidedly — at that
 * point the divergence is visible to the customer in real time and the trade-off changes.
 *
 * OBSERVABILITY (ATL-464): because failures are silent to the user, the dual-write failure rate
 * is the primary launch health signal. Every caught failure emits a STRUCTURED event:
 *  - Sentry tags `dual_write_module` / `dual_write_op` (+ legacy `dual_write` full label and the
 *    `supabase_dual_write_error` boolean) so a dashboard can chart failure rate by module,
 *  - a Sentry `fingerprint` of [supabase-dual-write, module, op] so all failures of one operation
 *    group into a single issue (clean rate-by-module view + per-op alerting),
 *  - level `warning` (non-fatal — Firestore is correct), keeping these out of the crash/error budget,
 *  - the entity ids (firestoreId/supabaseId) and any extra context the caller passes, which is what
 *    the drift-reconciliation job (ATL-469) needs to find and re-push the affected record,
 *  - a JSON-structured console line (parseable if web/native logs are shipped).
 *
 * See docs/launch/dual-write-observability.md for the Sentry saved query, dashboard and alert.
 *
 * Mirrors the pre-existing best-effort pattern in `dualWriteUpdate` (updates.ts).
 */
import * as Sentry from '@sentry/react-native'
import type { ReplayableRequestConfig } from '@atlas/api-client'
import {
  getRemoteConfigFetchTimeMillis,
  getRemoteConfigValue,
  refetchRemoteConfig,
  remoteConfigReady,
} from './firebaseFirestore'
import { getOrganization } from './types'
import {
  MIRROR_INTENT_ALLOWLIST_KEY,
  MIRROR_INTENT_SB_READERS_KEY,
  resetMirrorIntentBackendPolicyForTests,
  setMirrorIntentBackendPolicy,
} from './mirrorIntentPolicy'
import { probeDeletedEntity } from './deletedEntityLocation'
import type { DeletedEntityLocation } from './deletedEntityLocation'
import {
  drainDualWriteOutbox,
  enqueueBehindQueuedEntries,
  enqueueDualWriteFailure,
  isDeadBackendFailure,
  isDeleteLabel,
  isEntityAbsentFailure,
  isForbiddenFailure,
  isMigrationPausedFailure,
  isPermanentReplayFailure,
  isRouteNotFoundFailure,
  noteDualWriteSuccess,
  outboxHoldsDocument,
} from './dualWriteOutbox'
import { failureStatus, isRefusedWriteFailure, refusalCode, reportRefusedWrite } from './dualWriteRefusal'

// ============================================================================
// Dual-write gate (ATL-484 / ATL-466 canary allowlist)
// ============================================================================
//
// Dual-write is best-effort and fires on EVERY device/org that has the build. For the canary
// launch we need to (a) scope dual-write to a chosen allowlist of orgs and (b) have a global
// off-switch — neither existed before this gate. Both are driven by Firebase Remote Config so
// they can be changed WITHOUT an app build (propagates on the next config fetch).
//
// Remote Config keys:
//  - `dual_write_enabled` (string/bool): the master kill-switch. Only an explicit falsey value
//    ('false' / '0' / 'off' / 'no') disables dual-write globally. Unset/empty ⇒ enabled. This is
//    the instant panic button (ATL-468 rollback): set it false to halt ALL dual-write everywhere.
//  - `dual_write_org_allowlist` (string): canary scoping.
//      • unset / empty / '*' ⇒ NO allowlist enforcement → every org dual-writes (today's behavior,
//        preserved so existing e2e/smoke runs that rely on dual-write firing keep passing).
//      • JSON array e.g. '["GQEz...","bKHy..."]' ⇒ ONLY orgs whose Firestore id (or Supabase uuid)
//        is in the array dual-write; every other org no-ops silently (no Sentry noise from the
//        ~5k unmigrated orgs that would otherwise fail entity resolution).
//
// CANARY DEPLOY: set `dual_write_org_allowlist` to the canary org ids in Remote Config BEFORE the
// build reaches devices → only those orgs dual-write. ROLLBACK: remove an org from the list (org
// level) or set `dual_write_enabled=false` (global). Reads are unaffected throughout (Firestore).
//
// COLD-START behavior on an EMPTY/unfetched allowlist is environment-split (Remote Config fetches
// asynchronously at boot — getValue returns '' until fetchAndActivate resolves):
//  - PRODUCTION bundles fail CLOSED (deny → no dual-write) until the allowlist has loaded, so the
//    canary scope can't be bypassed in the brief pre-fetch window on first load (which would let
//    every org dual-write, flooding the failure signal with unmigrated orgs). Once Remote Config
//    activates, the real allowlist applies. A total RC fetch failure therefore = dual-write off,
//    which is the safe direction (Firestore is the source of truth regardless).
//  - DEV/TEST bundles fail OPEN (every org) so local development and the e2e/smoke suite keep
//    dual-writing without needing Remote Config configured.
// An explicit '*' (or '["*"]') always means all, in every environment; an explicit '[]' means
// deny-all without touching dual_write_enabled.

/** Whether this is a production bundle (vs dev/test). Drives the cold-start fail-open vs fail-closed split. */
function isProductionBuild(): boolean {
  try {
    if (typeof __DEV__ !== 'undefined') return !__DEV__
  } catch {
    /* __DEV__ not defined in this runtime */
  }
  return process.env.NODE_ENV === 'production'
}

/** Coerce the cross-platform Remote Config value (web: string; native: value object) to a string. */
function remoteConfigString(key: string): string {
  try {
    const v = getRemoteConfigValue(key)
    if (typeof v === 'string') return v
    if (v && typeof v === 'object' && 'asString' in v && typeof (v as { asString?: unknown }).asString === 'function') {
      return String((v as { asString: () => unknown }).asString())
    }
    return ''
  } catch {
    return ''
  }
}

/**
 * ATL-911 — the second policy source, because the first one lies.
 *
 * The gate had exactly one way to learn the policy: the Firebase Remote Config SDK on the device.
 * On a minority of iOS devices that SDK reports a SUCCESSFUL fetch and reads an EMPTY value in the
 * same tick, for whole sessions, for weeks. Measured live: three devices whose PostHog super
 * properties read `rc_status='fetched'` with `dual_write_allowlist_raw=''` for 20 consecutive days
 * while the Remote Config template served `"*"` to everyone, with no conditions. Nine sales at two
 * orgs are permanently absent from Supabase as a result, and the Sentry payload agrees with the
 * telemetry: `rcFetchAgeMs` on those events ranges 2ms–86s, i.e. a fetch that had just succeeded.
 *
 * Two beliefs this code was built on are therefore false, and both are load-bearing:
 *   • a successful fetch does NOT imply the values are readable, and
 *   • a failed fetch does NOT imply they are not (the same device read `"*"` in an `error` session).
 *
 * The obvious remedy — remember the last non-empty value the device read — cannot work here: two
 * of the three affected devices have NEVER read a non-empty value in any session on record, so
 * there is nothing to remember. The fix has to stop depending on the device's Remote Config read.
 *
 * So: when the SDK gives us nothing, ask our own backend, which reads the SAME Remote Config
 * template with the admin SDK. This adds no new authority — an operator still changes policy in
 * one place, and a kill-switch now reaches devices it previously could not reach at all (an empty
 * `dual_write_enabled` reads as ENABLED, so failing closed on the allowlist was never giving the
 * operator control over these devices; it was only making them silently lossy). The device must
 * reach the backend for the dual-write anyway, so the policy is knowable exactly when it matters.
 */
interface BackendDualWritePolicy {
  enabledRaw: string
  allowlistRaw: string
  /**
   * DEVICE clock at receipt, deliberately not the backend's `fetchedAt`. Freshness is compared
   * against `Date.now()` on this device, and a till with a skewed clock would otherwise make a
   * just-fetched policy look ancient (or a stale one look fresh). The backend caches its template
   * read for 60s, so the age this understates by is bounded by that.
   */
  fetchedAt: number
}
let backendPolicy: BackendDualWritePolicy | null = null
let backendPolicyInFlight: Promise<void> | null = null
let lastBackendPolicyAt = 0

/** Same throttle as the forced RC refetch: at most one attempt per interval per session. */
const BACKEND_POLICY_MIN_INTERVAL_MS = 5 * 60 * 1000
/** Same cap as the forced RC refetch, for the same reason: never hang checkout on a network call. */
const BACKEND_POLICY_WAIT_MS = 3000

/** Where the policy currently being enforced came from — carried in telemetry so the fix is provable. */
export type DualWritePolicySource = 'remote-config' | 'backend' | 'none'

/** TEST-ONLY: drop the fetched backend policy and its throttle (session-scoped by design). */
export function resetBackendPolicyForTests(): void {
  backendPolicy = null
  resetMirrorIntentBackendPolicyForTests()
  backendPolicyInFlight = null
  lastBackendPolicyAt = 0
}

/**
 * A policy value, from Remote Config when it reads one and from the backend fallback when it does
 * not. Remote Config always WINS when it has a value: the fallback stands in for a broken channel,
 * it does not override a working one.
 */
function policyString(key: 'dual_write_enabled' | 'dual_write_org_allowlist'): string {
  const live = remoteConfigString(key)
  if (live.trim()) return live
  if (!backendPolicy) return ''
  return key === 'dual_write_enabled' ? backendPolicy.enabledRaw : backendPolicy.allowlistRaw
}

/** Which source `policyString` is currently answering from. */
export function describePolicySource(): DualWritePolicySource {
  if (remoteConfigString('dual_write_org_allowlist').trim()) return 'remote-config'
  return backendPolicy ? 'backend' : 'none'
}

/**
 * Fetch the policy from the backend, bounded and throttled and shared, exactly like the forced RC
 * refetch above.
 *
 * A malformed or partial response is IGNORED rather than absorbed — half a policy would be
 * indistinguishable from an operator who allows nothing, which is the failure being fixed.
 */
async function awaitBoundedBackendPolicyFetch(): Promise<void> {
  if (!backendPolicyInFlight && Date.now() - lastBackendPolicyAt >= BACKEND_POLICY_MIN_INTERVAL_MS) {
    lastBackendPolicyAt = Date.now()
    backendPolicyInFlight = (async () => {
      // Lazy `require`, not a top-level import and not dynamic `import()` — the same call-time
      // require the outbox drain uses, for the same two reasons: `apiClient.js` imports
      // `firebase.ts`, which imports this module, so a top-level import would close that cycle
      // at module-init time on the hottest write path; and dynamic `import()` rejects under jest
      // without --experimental-vm-modules, which would make this path untestable AND silently
      // dead-on-arrival in the suite that is supposed to prove it works.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { api } = require('../API/apiClient') as {
        api: { get: (url: string) => Promise<{ data?: unknown }> }
      }
      const response = await api.get('/config/dual-write-policy')
      const body = (response as { data?: unknown })?.data as
        | {
            dualWriteEnabled?: unknown
            orgAllowlist?: unknown
            mirrorIntentOrgAllowlist?: unknown
            mirrorIntentSbReaders?: unknown
            templateVersion?: unknown
          }
        | undefined
      if (typeof body?.dualWriteEnabled !== 'string' || typeof body?.orgAllowlist !== 'string') return
      backendPolicy = {
        enabledRaw: body.dualWriteEnabled,
        allowlistRaw: body.orgAllowlist,
        fetchedAt: Date.now(),
      }
      // ATL-2052: the mirror-intent keys ride on the same response (absent from a backend older than
      // ATL-2052 → '' — "unset", never a decision). Published for the intent journal.
      setMirrorIntentBackendPolicy({
        allowlistRaw: typeof body.mirrorIntentOrgAllowlist === 'string' ? body.mirrorIntentOrgAllowlist : '',
        sbReadersRaw: typeof body.mirrorIntentSbReaders === 'string' ? body.mirrorIntentSbReaders : '',
        templateVersion: typeof body.templateVersion === 'string' ? body.templateVersion : null,
        receivedAt: backendPolicy.fetchedAt,
      })
      Sentry.addBreadcrumb({
        category: 'dual_write.gate',
        level: 'info',
        message: 'policy recovered from the backend (Remote Config read empty)',
        data: { allowlistRaw: backendPolicy.allowlistRaw.slice(0, 200) },
      })
      // A fetch that lands after a caller's wait cap still heals the session — same contract as
      // the RC refetch above.
      if (pendingUninformedSkips.length && describeGateDecision() === 'allowed') {
        void drainPendingUninformedSkips().catch(() => undefined)
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        backendPolicyInFlight = null
      })
  }
  const inFlight = backendPolicyInFlight
  if (!inFlight) return
  let waitTimer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    inFlight,
    new Promise<void>((resolveWait) => {
      waitTimer = setTimeout(resolveWait, BACKEND_POLICY_WAIT_MS)
    }),
  ]).finally(() => clearTimeout(waitTimer))
}

/**
 * ATL-2052 — at startup (after sign-in, `mirrorIntentTelemetry.ts`), make the mirror-intent policy
 * knowable on a device whose Remote Config reads it empty, BEFORE the first sale asks for it.
 *
 * `saveNewSale` asks `isMirrorIntentActive` synchronously before its commit, and the dual-write
 * gate's own backend fetch (`resolveGateDecision`) runs only after that, and only when the gate
 * itself is uninformed. So on an ATL-911 device the first sale of a launch would otherwise run
 * without its intent. This runs the same bounded, throttled, shared fetch — only when the device
 * read BOTH mirror-intent keys empty and this session has no backend policy yet — and waits for it.
 *
 * A failed fetch (offline) does NOT consume the gate's throttle: the throttle is released so the
 * first gated write can still ask the backend immediately, exactly as before this existed.
 * Never rejects.
 */
export async function primeMirrorIntentPolicyFromBackend(): Promise<'device-rc' | 'already' | 'fetched' | 'unavailable'> {
  try {
    await remoteConfigReady
    if (remoteConfigString(MIRROR_INTENT_ALLOWLIST_KEY).trim() || remoteConfigString(MIRROR_INTENT_SB_READERS_KEY).trim()) {
      return 'device-rc'
    }
    if (backendPolicy) return 'already'
    const startedHere = !backendPolicyInFlight && Date.now() - lastBackendPolicyAt >= BACKEND_POLICY_MIN_INTERVAL_MS
    await awaitBoundedBackendPolicyFetch()
    const inFlight = backendPolicyInFlight
    if (inFlight) await inFlight
    if (backendPolicy) return 'fetched'
    if (startedHere) lastBackendPolicyAt = 0
    return 'unavailable'
  } catch {
    return 'unavailable'
  }
}

/** Master kill-switch: only an explicit falsey Remote Config value disables dual-write globally. */
function isDualWriteEnabledGlobally(): boolean {
  const raw = policyString('dual_write_enabled').trim().toLowerCase()
  return raw !== 'false' && raw !== '0' && raw !== 'off' && raw !== 'no'
}

/** Parse the org allowlist. `all` ⇒ no enforcement (every org); otherwise only ids in the set. */
function parseOrgAllowlist(): { all: boolean; ids: Set<string>; unknown?: boolean } {
  const raw = policyString('dual_write_org_allowlist').trim()
  if (raw === '*' || raw === '"*"') return { all: true, ids: new Set() }
  if (!raw) {
    // Empty: Remote Config not yet activated (cold start) or genuinely unset. Prod fails CLOSED
    // (deny until the allowlist loads), dev/test fails OPEN (all) so local + e2e keep dual-writing.
    //
    // ATL-775: `unknown: true` marks this as "we do not KNOW the policy", which is a different
    // thing from "the policy says no" — and the difference is the whole ticket. A deny here loses
    // the write permanently and silently, so `describeGateDecision` reports it under its own
    // reason and the caller escalates it to Sentry, while a genuine not-in-allowlist stays quiet.
    return isProductionBuild()
      ? { all: false, ids: new Set(), unknown: true }
      : { all: true, ids: new Set() }
  }
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      if (parsed.includes('*')) return { all: true, ids: new Set() }
      return { all: false, ids: new Set(parsed.filter((x): x is string => typeof x === 'string')) }
    }
  } catch {
    // Malformed allowlist: fall through to fail-OPEN (don't silently halt dual-write on a typo —
    // the customer is safe regardless, and a halted canary is harder to notice than extra writes).
  }
  return { all: true, ids: new Set() }
}

// Log the resolved gate decision once per (orgId, decision) so the canary state is visible in logs
// without spamming on every write.
const loggedGateDecisions = new Set<string>()

/**
 * Whether the current org may dual-write to Supabase right now. Consulted by every best-effort
 * dual-write path (`bestEffortDualWrite` here + `dualWriteUpdate` in updates.ts).
 */
/**
 * ATL-775 — why the gate decided what it decided, so a SKIP stops being invisible.
 *
 * A skipped dual-write leaves no HTTP request, no Sentry event (`bestEffortDualWrite` returns
 * before its try/catch), and no `/compare` discrepancy (there is nothing to compare — the record is
 * simply absent). The only instrument that could ever see it was a fidelity sweep days later, which
 * is how ИП Калачев lost three revisions and ресторан Марракеш lost 2 of 2 sales with ZERO Sentry
 * events. That also means every "N dual-write failures in the last 7 days" figure in this migration
 * is a lower bound of unknown tightness.
 *
 * The four reasons are deliberately NOT collapsed into a boolean:
 *
 *   `allowed`            — write proceeds.
 *   `global-kill-switch` — `dual_write_enabled=false`. An operator turned it off ON PURPOSE; quiet.
 *   `not-in-allowlist`   — the allowlist loaded and this org is not on it. A real, INFORMED policy
 *                          decision; quiet.
 *   `rc-unavailable`     — the allowlist is EMPTY in a production build, i.e. Remote Config has not
 *                          activated (cold start) or its fetch errored. We are not denying because
 *                          policy says no; we are denying because we do not know the policy, and
 *                          the write is lost permanently. THIS ONE IS ESCALATED.
 *   `rc-stale`           — the allowlist DID load and denies this org, but the last successful
 *                          Remote Config fetch is older than the trust bound and a forced refetch
 *                          could not refresh it. The value being enforced may be a year-old canary
 *                          cohort (ATL-786: devices held the pre-18-Jun 9-org list under a live
 *                          `"*"` and skipped every write "by policy", quietly). A deny on ancient
 *                          information is not an informed decision. THIS ONE IS ESCALATED.
 */
export type DualWriteGateReason =
  | 'allowed'
  | 'global-kill-switch'
  | 'not-in-allowlist'
  | 'rc-unavailable'
  | 'rc-stale'

/**
 * How old the last SUCCESSFUL Remote Config fetch may be before a not-in-allowlist deny stops
 * being trusted as policy (ATL-786). The allowlist is fetched with a 60s minimum interval and on
 * every app foreground, so on a healthy device it is minutes old; a policy this stale means the
 * device has not managed one successful fetch all day and may be enforcing an arbitrarily old
 * cohort. 24h keeps the bound far above fetch throttling and far below the month-old values
 * observed live.
 */
export const ALLOWLIST_TRUST_BOUND_MS = 24 * 60 * 60 * 1000

/**
 * True when the values being ENFORCED are older than the trust bound (or were never fetched).
 *
 * ATL-911: the age must come from whichever source produced the values. A device reading its
 * policy from the backend has a perfectly fresh policy while its Remote Config SDK fetch time
 * says whatever it says — and, in the case that motivated the fallback, that fetch time is
 * RECENT AND WRONG, so trusting it would silently re-import the broken channel's opinion into
 * the freshness decision the fallback exists to rescue.
 */
export function isRemoteConfigPolicyStale(): boolean {
  const fetchedAt =
    describePolicySource() === 'backend'
      ? (backendPolicy?.fetchedAt ?? 0)
      : getRemoteConfigFetchTimeMillis()
  return fetchedAt <= 0 || Date.now() - fetchedAt > ALLOWLIST_TRUST_BOUND_MS
}

/**
 * Throttle for write-time forced refetches (ATL-786): at most one attempt per interval per
 * session, so a device whose RC endpoint is down does not add a network round-trip to every
 * sale in a rush hour. Module-level, like the other per-session gate state here.
 */
const STALE_REFETCH_MIN_INTERVAL_MS = 5 * 60 * 1000
let lastStaleRefetchAt = 0

/**
 * How long ONE write waits for the forced refetch before giving up (review round 2, M1).
 * The SDK's own fetch timeout is 60s — awaiting that uncapped would freeze checkout for a
 * minute on exactly the devices whose RC endpoint is broken. Same 3s bound (and rationale) as
 * the outbox-enqueue race in bestEffortDualWrite: never hang checkout. A refetch that
 * completes AFTER the cap still activates — the next write sees the fresh policy and drains
 * the pending-skip queue.
 */
const STALE_REFETCH_WAIT_MS = 3000

/** Shared in-flight refetch (round 2, M4): concurrent writes wait on ONE fetch instead of the
 *  late ones insta-denying against the old config while the first write's refetch is airborne. */
let staleRefetchInFlight: Promise<unknown> | null = null

/** TEST-ONLY: reset the stale-refetch throttle (session-scoped by design, leaks across tests). */
export function resetStaleRefetchThrottleForTests(): void {
  lastStaleRefetchAt = 0
  staleRefetchInFlight = null
}

/** Kick (or join) the throttled refetch and wait for it, bounded by STALE_REFETCH_WAIT_MS. */
async function awaitBoundedStaleRefetch(): Promise<void> {
  if (!staleRefetchInFlight && Date.now() - lastStaleRefetchAt >= STALE_REFETCH_MIN_INTERVAL_MS) {
    lastStaleRefetchAt = Date.now()
    staleRefetchInFlight = refetchRemoteConfig()
      .then(() => {
        // A refetch that lands AFTER a caller's wait cap still heals the session: if the
        // fresh policy allows, replay the skips queued while it was stale — without waiting
        // for the next write to happen by.
        if (pendingUninformedSkips.length && describeGateDecision() === 'allowed') {
          void drainPendingUninformedSkips().catch(() => undefined)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        staleRefetchInFlight = null
      })
  }
  const inFlight = staleRefetchInFlight
  if (!inFlight) return
  let waitTimer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    inFlight,
    new Promise<void>((resolveWait) => {
      waitTimer = setTimeout(resolveWait, STALE_REFETCH_WAIT_MS)
    }),
  ]).finally(() => clearTimeout(waitTimer))
}

/**
 * The full async gate: settle Remote Config (ATL-775), evaluate, and refuse to treat a deny
 * built on stale (or absent) policy as informed (ATL-786). EVERY dual-write path must consult
 * this — the sync `shouldDualWrite()` cannot heal a stale cohort and exists for telemetry
 * snapshots only.
 *
 * Staleness is a property of the POLICY SNAPSHOT, not of one deny reason (review round 2, M3):
 * a stale cached `dual_write_enabled=false` kill-switch is exactly as uninformed as a stale
 * allowlist cohort, and `rc-unavailable` (no policy at all) deserves the same mid-session
 * retry. Only a deny backed by a FRESH policy is accepted as an operator's decision.
 */
export async function resolveGateDecision(): Promise<DualWriteGateReason> {
  // ATL-775: settle Remote Config BEFORE consulting the gate — parseOrgAllowlist() reads an
  // EMPTY string until RC activates, and a production build fails CLOSED on empty. Awaiting
  // turns a silent cold-start drop into a short wait; `remoteConfigReady` never rejects.
  // Deliberately NOT a fail-open flip: waiting is correct under BOTH policies, flipping is not.
  await remoteConfigReady
  const gate = describeGateDecision()
  if (gate === 'allowed') return gate

  const uninformed = gate === 'rc-unavailable' || isRemoteConfigPolicyStale()
  if (!uninformed) return gate // a fresh-policy deny IS the policy — informed, accepted.

  // The deny rests on information the device has not refreshed within the trust bound (or
  // never had). Refetch — bounded, throttled, shared — and re-ask before accepting it.
  //
  // ATL-911: with ONE exception. `rc-unavailable` while the SDK reports a RECENT SUCCESSFUL fetch
  // is the signature of the broken channel — the SDK has already fetched, successfully, and still
  // reads empty. Refetching that is asking the same liar the same question, at the cost of up to
  // BACKEND_POLICY_WAIT_MS + STALE_REFETCH_WAIT_MS on a live checkout. Go straight to the source
  // that can actually answer.
  const sdkClaimsFreshFetch = gate === 'rc-unavailable' && !isRemoteConfigPolicyStale()
  if (!sdkClaimsFreshFetch) await awaitBoundedStaleRefetch()
  let refreshed = describeGateDecision()

  if (refreshed === 'rc-unavailable') {
    await awaitBoundedBackendPolicyFetch()
    refreshed = describeGateDecision()
  }

  if (refreshed === 'allowed' || refreshed === 'rc-unavailable') return refreshed
  // Still denied: if the policy is now fresh the deny is informed; otherwise it is rc-stale.
  return isRemoteConfigPolicyStale() ? 'rc-stale' : refreshed
}

/**
 * In-session replay of uninformed skips (review round 2, M2). A gate-denied write never ran,
 * so the durable outbox cannot capture it (replay needs the executed request's
 * `error.requestConfig`; there is none pre-flight) — but the CALLER'S CLOSURE is right here.
 * Holding it means a later heal (the bounded refetch landing, a foreground refetch, the next
 * allowed write) replays the skipped writes through the normal bestEffortDualWrite path —
 * where a real failure then DOES reach the outbox. Session-scoped by nature: a session that
 * dies before healing still loses these (they were escalated to Sentry when skipped, and the
 * org-level reconcile campaign is the durable backstop).
 *
 * TWO CLASSES ARE QUEUED, and the exclusion of everything else is the load-bearing part.
 *
 *  • CREATE-class, unconditionally (delta review, finding 1). Creates are inserts: they cannot
 *    clobber, and they are the COMPLETENESS class — a lost create is a missing row forever (the
 *    evidenced ATL-786 loss was sales.create).
 *
 *  • DELETE-class CARRYING A DOCUMENT IDENTITY (ATL-1192). A gate-denied delete is the other
 *    completeness class, and the worse-shaped one: Firestore has dropped the row, so the write is
 *    not drift that a later write of the same document repairs — nothing will ever write that
 *    document again, and at the read cutover the customer's deleted row comes back. It was
 *    excluded here only as collateral of the create-only rule, whose two premises a delete does
 *    not share: it is not an absolute value that a newer live write can be clobbered by (it is
 *    terminal), and every delete-class call site DOES pass `{ firestoreId }` (4 of them
 *    through `bestEffortDualWriteDelete`, whose signature requires it). The identity
 *    is REQUIRED rather than derived — the same rule 1 the outbox enforces — because it is the
 *    supersede key that makes the two guards below possible; a delete without one is refused
 *    loudly and stays lost. The last such site was `editableConstant.delete`, which now states
 *    `item.id` (ATL-1207) — and still falls back to the refusal for an item that has no id.
 *
 * Everything else is still excluded, for the original reason: most call sites pass no document
 * identity, so the queue cannot enforce a per-document supersede rule, and replaying an
 * ABSOLUTE-VALUE update (bonus balance, stock mirror, cashbox amounts) after a newer live write
 * for the same document would clobber it with the stale value. A lost update is fidelity drift
 * that the next write of that document, or the fidelity sweep, repairs.
 *
 * ADMITTING DELETES NEEDS TWO GUARDS, because a wrongly replayed delete DESTROYS a live row —
 * strictly worse than the loss being fixed, so both fail toward dropping the delete:
 *
 *  1. RECREATE. A LIVE write that lands for the same document supersedes a queued delete for it
 *     (`purgePendingSkipsForDocument`). The sequence is real: the delete is denied, the cashier
 *     restores the product under the same Firestore id, that write lands — and the queued delete
 *     would then remove the restored row. "Live" is the load-bearing word and it cost a critical
 *     review finding to get right: a replay of an entry queued EARLIER is not evidence about the
 *     document's present state, and treating it as such lets a replayed create delete the delete
 *     that followed it. See `dualWrite`'s docblock.
 *
 *     TWO LIMITS THIS GUARD HAS, named because a guard's edges are the part that gets assumed
 *     away. Neither is fixed here and neither is new to this queue:
 *       – it is SESSION-SCOPED, so it cannot SEE the recreate; ATL-1207 gave it a second source
 *         that can. The queue is a module-level array with no Web Locks lease and no cross-device
 *         view (contrast outbox rule 7), so a document deleted on this till and re-created on
 *         ANOTHER till, or in another browser tab, used to be invisible to it and that replay
 *         destroyed the live row. Reachable only where ids are chosen rather than minted — the
 *         user-typed constants (`paymentMethods`, `wallets`, `roles`, …), since Firestore mints a
 *         fresh id per document everywhere else — and those call sites now state `deletedFrom`, so
 *         the drain re-reads the entity from Firestore (the store both tills converge on) before
 *         replaying and withdraws the delete if it is back. What is still session-scoped is the
 *         QUEUE, not the evidence: an entry whose probe cannot be answered is held and is lost if
 *         the session ends first. The durable outbox carries the identical exposure over a
 *         SEVEN-DAY window and is guarded by the same probe (outbox rule 8).
 *       – it matches on the bare document id, with no module scoping, so a live write in one
 *         entity family can purge a queued delete in another whose id happens to collide
 *         (`checkIdUniquenes` enforces uniqueness only across payment/wallet/purchase methods).
 *         Deliberate: scoping it by module would make the guard MISS the cases where one document
 *         is written under two labels (`workingHours.deletePaymentTransaction` vs
 *         `transactions.*`), and a missed purge destroys a live row while an extra purge only
 *         re-opens this ticket's loss for one document. It fails toward loss on purpose.
 *  2. RE-CHECK BEFORE REPLAY. The drain takes each entry from the LIVE queue rather than from a
 *     snapshot, so guard 1 can still act on an entry the drain has not reached yet. Outbox rule 4,
 *     for the same reason and with the same residual: the single in-flight replay. Since ATL-1207
 *     an entry can also be OUT of the queue while its recreate probe runs — a server round-trip
 *     ahead of the replay — so guard 1's purges are recorded for the pass as well
 *     (`purgedDuringPendingSkipDrain`) and applied to the entry being probed and to every entry
 *     the probe could not answer for. Without that, the guard added to close the cross-device
 *     race opens a same-session one of exactly the same shape.
 *
 * ORDERING is FIFO and that is sufficient here — deliberately WITHOUT the outbox's cross-label
 * "a delete supersedes the whole document" rule, which exists there to stop a queued create from
 * resurrecting a deleted row. A create queued before a delete replays before it, so Supabase
 * converges on absent. Dropping the create instead would leave a delete replaying against a row
 * that was never inserted — a 404 (treated as delivered since ATL-1193, but still a request for
 * nothing). Supersede here is
 * therefore per (label, document): a repeated delete of one document replaces the older statement
 * of the same fact.
 *
 * FIFO IS A CLAIM ABOUT WHAT LANDS, NOT ABOUT WHAT STARTS, and the drain has to earn it — an
 * earlier revision of this paragraph asserted it flatly while `REPLAY_ENTRY_CAP_MS` was quietly
 * falsifying it (round-2 review finding A). It is earned by `abandonedReplaysInFlight`: a replay
 * the cap abandons is still airborne, so the queue refuses to start ANY further replay until it
 * settles, and its settlement re-kicks the drain. Two weaker versions were tried and both were
 * wrong — a bigger cap constant (nothing bounds the transport: it re-dispatches on 401 with a
 * fresh timeout and the auth fetch precedes axios's clock), and breaking out of the pass (the next
 * live write simply restarts the drain seconds later). The residual is stated rather than claimed
 * away: a replay that NEVER settles holds the queue for the rest of the session, and those deletes
 * are then lost — chosen deliberately, because the alternative is a replay overtaking an airborne
 * one and destroying a live row. Held entries also sit at the FRONT of the queue, so a renewed
 * deny window that fills it evicts them first.
 *
 * THREE RESIDUALS ADMITTING DELETES LEAVES, measured rather than assumed, each filed separately
 * so none of them hides behind this comment:
 *
 *  • A replayed delete whose row was NEVER MIRRORED gets an entity-absent 404. Since ATL-1195
 *    every `*.delete` site goes through `bestEffortDualWriteDelete` (an id-less
 *    `editableConstant.delete` aside), whose closure swallows it as the free, expected outcome it
 *    is — on the live path and on this queue's replay alike. The DURABLE outbox replays the
 *    serialized request, not the closure; until ATL-1193 its drain classified that as transient, burned
 *    the entry's attempts and dropped it loudly as a permanent loss that never existed. The
 *    drain now applies the same classifier and removes it as delivered.
 *  • The queue cap is shared. 200 entries per session, oldest-first eviction, so on a till that
 *    gate-denies hundreds of writes a delete can be evicted by create volume; the eviction is a
 *    breadcrumb, not an event.
 *  • `editableConstant.delete` states `item.id` since ATL-1207 (it is the LIVE path for five
 *    reusable-id families, so it had to), but only when the item carries one — an id-less item
 *    still states nothing and is still lost outright.
 *
 * Double-apply is excluded by the SINGLE-EXECUTION invariant, not by payload-id idempotency
 * (which most but not all creates also have): a gate-denied fn was never executed, a drained
 * entry is removed from the queue before its one replay, and a re-queue only happens on a
 * gate-deny where fn again did not run. Residual edge (documented, accepted): a create with NO
 * identity replayed after the same document was deleted live re-inserts it — a Supabase-only
 * orphan the sweep's pruneOrphans pass detects. Guard 1 cannot see it, because the entry has no
 * key to match on.
 */
interface PendingUninformedSkip {
  label: string
  fn: () => Promise<unknown>
  context?: DualWriteContext
  /**
   * The document this entry is about — the supersede key (ATL-1192). Present on delete-class
   * entries, which are admitted only when the caller states one; absent on creates, most of which
   * pass no context at all.
   */
  firestoreId?: string
  /**
   * ATL-1207 — where the deleted entity lived in Firestore, when the caller states it, plus the
   * org that owns it. The pair is what lets the drain ask the shared source of truth whether
   * another device has recreated the entity since this delete was denied; without the org the
   * probe would be comparing this session's data against another org's document, so it refuses.
   */
  deletedFrom?: DeletedEntityLocation
  orgFirestoreId?: string
  /**
   * ATL-1207 — do not re-ask Firestore about this entry before this instant. Set when a probe goes
   * unanswered. The drain is kicked by EVERY successful live dual-write, so on a till that is
   * selling while it cannot reach Firestore an unheld entry would mean one server read per sale,
   * each of them waiting out the probe's own cap. The outbox's equivalent is `probeHolds`; this is
   * the same idea with the same purpose, flat rather than doubling because this queue lives for a
   * session rather than a week.
   */
  probeHeldUntil?: number
}
const MAX_PENDING_UNINFORMED_SKIPS = 200
/** How long an unanswered recreate probe parks its entry before the drain asks again (ATL-1207). */
const PENDING_SKIP_PROBE_HOLD_MS = 30_000
const pendingUninformedSkips: PendingUninformedSkip[] = []
let pendingSkipDrainRunning = false
/**
 * Documents a LIVE write landed for while a drain pass is running (ATL-1207).
 *
 * Guard 1 (`purgePendingSkipsForDocument`) can only withdraw entries that are IN the queue, and a
 * pass takes its entries OUT of it: the entry being probed and every entry the probe could not
 * answer for exist only in the pass's own arrays. Before ATL-1207 that exposure was the width of
 * the replay itself; the probe is a server round-trip AHEAD of the replay, so an entry can now sit
 * outside the queue for a full `PROBE_TIMEOUT_MS` — with a read taken BEFORE the live write —
 * and then fire over the row that write just created. That is the loss this ticket exists to
 * prevent, arrived at through its own guard.
 *
 * So the purge records what it saw here as well, and the pass consults it after the probe and
 * again before it restores the held entries. Non-null ONLY while a pass is running, so nothing
 * accumulates between them; a replay's own success cannot land in it, because the drain calls
 * `dualWrite(..., isPendingSkipReplay = true)` and that branch does not purge.
 */
let purgedDuringPendingSkipDrain: Set<string> | null = null

/**
 * Is this label a create-class operation (replay-safe insert)? Census of every
 * bestEffortDualWrite label (2026-07-29, 42 create-class ops): three shapes exist —
 * `create` (sales.create, …), `createX` (sales.createSaleReturn,
 * workingHours.createPaymentTransaction — the round-3 review caught these financial creates
 * being dropped by a literal `op === 'create'` match), and `X.create`
 * (products.auditLog.create — splitLabel keeps dots in the op). Every one is a genuine
 * insert; no `createOrUpdate`-style op exists.
 *
 * A write that is replay-safe for another reason declares it with `context.replaySafe` rather
 * than borrowing a create-shaped label (see DualWriteContext.replaySafe).
 */
function isReplaySafeCreateOp(op: string): boolean {
  return op === 'create' || op.startsWith('create') || op.endsWith('.create')
}

/** TEST-ONLY: clear the pending-skip queue (session-scoped by design, leaks across tests). */
export function resetPendingUninformedSkipsForTests(): void {
  pendingUninformedSkips.length = 0
  pendingSkipDrainRunning = false
  purgedDuringPendingSkipDrain = null
  abandonedReplaysInFlight = 0
}

/**
 * Per-session dedupe for the eviction event below. Same shape and same reason as
 * `reportedGateSkips`: the condition can repeat hundreds of times inside one outage, and one
 * event per occurrence would bury the signal it exists to raise.
 */
const reportedPendingSkipEvictions = new Set<string>()

/** TEST-ONLY: clear the per-session eviction dedupe (session-scoped by design). */
export function resetPendingSkipEvictionReportingForTests(): void {
  reportedPendingSkipEvictions.clear()
}

function rememberUninformedSkip(entry: PendingUninformedSkip): void {
  // Supersede per (label, document) — see the ordering paragraph in the docblock above for why
  // this is NOT the outbox's cross-label document purge. Only delete-class entries carry an
  // identity today, so in practice this replaces an older delete of the same document.
  if (entry.firestoreId) {
    for (let i = pendingUninformedSkips.length - 1; i >= 0; i--) {
      const queued = pendingUninformedSkips[i]
      if (queued.firestoreId === entry.firestoreId && queued.label === entry.label) {
        pendingUninformedSkips.splice(i, 1)
      }
    }
  }
  if (pendingUninformedSkips.length >= MAX_PENDING_UNINFORMED_SKIPS) {
    const dropped = pendingUninformedSkips.shift()
    const droppedLabel = dropped?.label ?? '?'
    Sentry.addBreadcrumb({
      category: 'dual_write.gate',
      level: 'warning',
      message: `pending-skip queue full — dropped oldest (${droppedLabel})`,
    })
    // An eviction is a PERMANENT loss of the write, and it was reported by breadcrumb only — i.e.
    // it could only ever be read as context on some *other* event, and on a session where no other
    // event fired it was invisible (round-1 review finding 5). Eviction is oldest-first, so during
    // an outage on a busy till the deletes queued early are the first to go: the class this ticket
    // exists to save is the one the cap destroys first. Deduped per label per session.
    if (!reportedPendingSkipEvictions.has(droppedLabel)) {
      reportedPendingSkipEvictions.add(droppedLabel)
      Sentry.captureMessage(`dual-write pending-skip evicted at the queue cap (${droppedLabel})`, {
        level: 'warning',
        tags: {
          dual_write_pending_skip_evicted: true,
          dual_write: droppedLabel,
        },
        fingerprint: ['dual-write-pending-skip-evicted', droppedLabel],
        contexts: {
          dual_write_gate: {
            label: droppedLabel,
            firestoreId: dropped?.firestoreId,
            cap: MAX_PENDING_UNINFORMED_SKIPS,
          },
        },
      })
    }
  }
  pendingUninformedSkips.push(entry)
}

/**
 * A live dual-write landed for these documents, so anything queued FOR THE SAME DOCUMENT must not
 * replay (ATL-1192, guard 1). Whatever the live write was, it proves the document exists in
 * Supabase NOW — an entry queued before it is a statement about an older state, and for the class
 * this ticket admits, replaying it would remove a row the customer can see. Dropping it re-opens
 * this ticket's own loss for that one document, which is the safe direction and not a repair: a
 * retained row is still DETECTABLE afterwards (the id-level set-diff and the census see it, and
 * the sweep's `pruneOrphans` pass would remove it if it were ever re-enabled — it is paused,
 * ATL-707), whereas a row this queue destroyed leaves no trace on either side.
 *
 * Scoped to the document rather than to the delete class deliberately. Deletes are the only
 * entries that carry an identity today, so the two are the same rule — and a `isDeleteLabel`
 * condition on top of that would be a branch no test could falsify, while being the WRONG answer
 * if creates ever start carrying one (a live delete must be able to purge a queued create, or it
 * resurrects the row).
 *
 * Synchronous by construction, and called BEFORE the drain is kicked on the same success, so the
 * purge cannot lose a race with the replay it is meant to prevent.
 */
function purgePendingSkipsForDocument(ids: readonly (string | undefined)[]): void {
  const keys = new Set(ids.filter((v): v is string => typeof v === 'string' && v.length > 0))
  if (keys.size === 0) return
  // ATL-1207 — recorded BEFORE the empty-queue early return below, and deliberately so: while a
  // drain pass is running the queue can be empty precisely because the pass is holding the very
  // entries this purge is about. See `purgedDuringPendingSkipDrain`.
  if (purgedDuringPendingSkipDrain) {
    for (const key of keys) purgedDuringPendingSkipDrain.add(key)
  }
  if (pendingUninformedSkips.length === 0) return
  const dropped: string[] = []
  for (let i = pendingUninformedSkips.length - 1; i >= 0; i--) {
    const entry = pendingUninformedSkips[i]
    if (!entry.firestoreId || !keys.has(entry.firestoreId)) continue
    pendingUninformedSkips.splice(i, 1)
    dropped.push(`${entry.label}:${entry.firestoreId}`)
  }
  if (dropped.length === 0) return
  // ONE breadcrumb for the whole purge, not one per entry (round-1 review finding 8): the queue
  // holds up to 200 entries and Sentry's default ring buffer is 100, so a large purge could flush
  // the gate diagnosis off whatever event fired next — the breadcrumbs this file emits precisely
  // so a skip can be diagnosed from the event alone.
  Sentry.addBreadcrumb({
    category: 'dual_write.gate',
    level: 'info',
    message: `pending-skip: dropped ${dropped.length} queued entr(y/ies) — a live write landed for the same document`,
    data: { dropped: dropped.slice(0, 20).join(','), droppedCount: dropped.length },
  })
}

/**
 * A destructive write was skipped AND could not be queued. Reported under the outbox's own
 * vocabulary (`no-document-identity`, rule 1) so both refusals can be counted together: the
 * `reportGateSkip` event says the write was lost, this says why nothing will replay it.
 */
const reportedUnqueueableSkips = new Set<string>()

/** TEST-ONLY: clear the per-session no-identity dedupe (session-scoped by design). */
export function resetUnqueueableSkipReportingForTests(): void {
  reportedUnqueueableSkips.clear()
}

function reportUnqueueableUninformedSkip(label: string, context?: DualWriteContext): void {
  // Deduped per label per session, like `reportedGateSkips` — a settings screen deleting many
  // constants in one outage would otherwise emit one breadcrumb per row and flush the ring buffer.
  if (reportedUnqueueableSkips.has(label)) return
  reportedUnqueueableSkips.add(label)
  Sentry.addBreadcrumb({
    category: 'dual_write.gate',
    level: 'warning',
    message: `pending-skip refused: no-document-identity (${label})`,
    data: { label, ...(context || {}) },
  })
}

/**
 * Report a throw that escaped `dualWrite` during a pending-skip REPLAY (round-1 review finding 6).
 *
 * Its own tag and fingerprint rather than `reportDeleteWrapperThrow`'s, because the two answer
 * different questions: that one means "the delete wrapper's plumbing threw on the live path", this
 * means "the drain lost an entry it had already removed from the queue". Same class of fault — the
 * machinery that reports failures failed — and the same consequence: the write is one-sided and
 * was NOT queued.
 */
function reportPendingSkipReplayThrow(
  label: string,
  error: unknown,
  context?: DualWriteContext,
): void {
  try {
  const { module, op } = splitLabel(label)
  // eslint-disable-next-line no-console
  console.error(
    `[dualWrite] ${label} pending-skip REPLAY threw outside the write (non-fatal — Firestore ` +
      `already committed, but this write is one-sided and was NOT queued)`,
    JSON.stringify({ module, op, ...(context || {}), error: errorMessage(error) }),
  )
  Sentry.captureException(error, {
    level: 'error',
    tags: {
      supabase_dual_write_error: true,
      dual_write: label,
      dual_write_module: module,
      dual_write_op: op,
      dual_write_pending_skip_replay_threw: true,
      dual_write_outbox_queued: false,
    },
    fingerprint: ['supabase-dual-write-pending-skip-replay-threw', module, op],
    contexts: { dual_write: { module, op, pendingSkipReplayThrew: true, ...(context || {}) } },
  })
  } catch {
    // Self-guarded, like `reportDeleteWrapperThrow`: a caller-supplied circular `context` would
    // make the `JSON.stringify` above throw, and a reporter that can take down its caller is the
    // fault it exists to report. Nothing left to do — reporting this would recurse.
  }
}

/**
 * How long one replay may hold the drain pass before it is abandoned and the pass ends.
 *
 * It was 30_000 — HALF `typedApiClient`'s 60s `writeTimeout`, the 60s ATL-776 raised it to
 * precisely because a 30s abort was dropping the largest carts at big orgs. So the cap fired on
 * replays that were merely SLOW, in exactly the band the transport exists to support, and a capped
 * entry is abandoned WHILE STILL IN FLIGHT: the drain moved to the next entry, and a replayed
 * create could then land after its own delete and resurrect the row (round-2 review finding A).
 *
 * 90s > 60s, so it no longer fires inside a single normal dispatch. THAT IS ALL IT BUYS, and the
 * distinction cost a round-3 finding: an earlier revision of this comment claimed 90s sat "above
 * the transport ceiling", so a capped replay could only be a client defect. False, measured —
 * `packages/api-client` re-dispatches on a 401 with a FRESH full timeout (ATL-526), the auth-token
 * fetch runs in a request interceptor before axios's clock starts and is bounded by nothing here,
 * and `resolveGateDecision()` adds up to ~6s inside the raced promise. One logical replay can
 * exceed 120s while perfectly alive.
 *
 * So ORDERING IS NOT ENTRUSTED TO THIS NUMBER any more — see `abandonedReplaysInFlight`, which
 * makes it safe by construction whatever the timings turn out to be. A constant cannot be proven
 * to sit above an unbounded ceiling, and reasoning that tries to is the shape this file keeps
 * getting caught by.
 */
export const REPLAY_ENTRY_CAP_MS = 90_000

/**
 * Replays the cap abandoned that have not settled yet.
 *
 * A capped replay is still airborne: nothing cancelled it, and it may still land. Starting another
 * replay beside it is what turns "abandoned" into "reordered" — a create landing after its own
 * delete — so while this is non-zero the pending-skip drain refuses to start at all. It is the
 * mechanism that makes FIFO a claim about what LANDS rather than about what starts, and it holds
 * without assuming anything about how long a request can take, which is the assumption that was
 * wrong twice.
 *
 * The abandoned replay's own settlement decrements this and re-kicks the drain, so the held
 * entries are not waiting on a live write happening by. If it NEVER settles the queue stays held
 * for the session: those deletes are then lost, which is this ticket's own failure mode — chosen
 * deliberately over the alternative, because a replay that overtakes an airborne one destroys a
 * live row, and every guard in this queue fails toward loss rather than destruction.
 */
let abandonedReplaysInFlight = 0

/** Sentinel for the cap arm of the replay race — a local symbol no write can return. */
const REPLAY_CAP_REACHED = Symbol('pending-skip-replay-cap')

/**
 * A gate-denied delete withdrawn because the entity is back (ATL-1207).
 *
 * An EVENT rather than a breadcrumb, for the same reason as the outbox's `reportRecreated`: this
 * is the one place a delete is abandoned on purpose, and "how often do two tills race a delete
 * against a recreate" has to be countable — a breadcrumb can only be read as context on some other
 * event, and a session where nothing else fires reports nothing at all (the round-1 finding that
 * made the queue-cap eviction an event).
 *
 * SELF-GUARDED, like `reportPendingSkipReplayThrow` and `reportDeleteWrapperThrow`: it is called
 * from inside the drain loop, at a point where the entries held by that pass are out of the queue
 * and exist only in the pass's own array. A reporter that can take down its caller here would take
 * those deletes with it.
 */
function reportPendingSkipWithdrawn(entry: PendingUninformedSkip): void {
  try {
    const detail = {
      label: entry.label,
      firestoreId: entry.firestoreId,
      orgFirestoreId: entry.orgFirestoreId,
      deletedFrom: entry.deletedFrom,
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[dualWrite] pending-skip WITHDREW ${entry.label} — the entity was recreated, replaying would destroy it`,
      JSON.stringify(detail),
    )
    Sentry.captureMessage(`dual-write pending-skip withdrew ${entry.label} (entity recreated)`, {
      level: 'warning',
      tags: {
        dual_write_pending_skip_recreated: true,
        dual_write: entry.label,
        ...(entry.orgFirestoreId ? { dual_write_org: entry.orgFirestoreId } : {}),
      },
      fingerprint: ['dual-write-pending-skip-recreated', entry.label],
      contexts: { dual_write_gate: detail },
    })
  } catch {
    // Nothing left to do — reporting this would recurse.
  }
}

/**
 * A queued delete withdrawn by GUARD 1 while the drain pass was holding it (ATL-1207).
 *
 * A breadcrumb rather than an event, unlike `reportPendingSkipWithdrawn`: this is the same
 * conclusion `purgePendingSkipsForDocument` already reaches by breadcrumb — a live write for this
 * document landed in this session — reached a few hundred milliseconds later only because the
 * entry happened to be out of the queue while its recreate probe ran. Counting it separately in
 * Sentry would inflate "two devices raced a delete against a recreate" with same-session traffic.
 *
 * Self-guarded for the same reason as its siblings: it is called from inside the drain loop and
 * from the `finally`, where the pass's entries exist nowhere else.
 */
function reportPendingSkipPurgedInFlight(entry: PendingUninformedSkip): void {
  try {
    Sentry.addBreadcrumb({
      category: 'dual_write.gate',
      level: 'info',
      message: `pending-skip: dropped ${entry.label} mid-drain — a live write landed for the same document`,
      data: { label: entry.label, firestoreId: entry.firestoreId },
    })
  } catch {
    // Nothing left to do — reporting this would recurse.
  }
}

async function drainPendingUninformedSkips(): Promise<void> {
  if (pendingSkipDrainRunning) return
  // Never start a replay beside one the cap abandoned but that is still airborne — see
  // `abandonedReplaysInFlight`. This is the ordering guarantee; the cap's VALUE is not.
  if (abandonedReplaysInFlight > 0) return
  pendingSkipDrainRunning = true
  // ATL-1207 — entries whose recreate probe could not be ANSWERED. They are put back at the
  // FRONT after the pass, in order, rather than re-queued at the back: they are still the oldest
  // statements about their documents, and the queue's FIFO claim is about what lands.
  //
  // Declared OUTSIDE the try, and restored in the `finally`, because these entries have already
  // been `shift()`ed out of the module-level queue: while the pass runs they exist nowhere else.
  // A throw anywhere in the loop — the drain's callers all `.catch(() => undefined)`, so it would
  // be silent — would otherwise destroy every held delete, which is the loss this guard exists to
  // prevent, arrived at from the other side.
  const heldUnverifiable: PendingUninformedSkip[] = []
  // ATL-1207 — guard 1's reach over the entries this pass has taken out of the queue. Armed for
  // the whole pass and disarmed in the `finally`, so a live write that lands while an entry is
  // being probed (or parked in `heldUnverifiable`) is still able to withdraw it.
  purgedDuringPendingSkipDrain = new Set<string>()
  try {
    // Bounded by the length AT ENTRY, and each entry is taken from the LIVE queue rather than
    // from a snapshot (ATL-1192, guard 2 — outbox rule 4): a live success landing mid-drain must
    // still be able to purge a queued delete the drain has not reached. The budget is what keeps
    // an entry re-denied during the drain — which re-queues itself at the BACK — from being
    // picked up again in the same pass, which is what the snapshot used to buy.
    let budget = pendingUninformedSkips.length
    while (budget-- > 0) {
      const entry = pendingUninformedSkips.shift()
      if (!entry) break
      // ATL-1207 — the cross-device half of guard 1. `purgePendingSkipsForDocument` withdraws a
      // queued delete when a live write for that document lands IN THIS SESSION; it cannot see the
      // manager re-adding the same payment method on another till, and this queue's own docblock
      // names that as a limit it did not close. Firestore is where the two tills meet, so the
      // delete's precondition — the entity is still gone — is re-read there before it fires.
      //
      // Only entries whose caller stated a location are checked, which is the reusable-id
      // population (`deletedEntityLocation`); everything else replays exactly as before.
      if (entry.deletedFrom && entry.firestoreId) {
        if (entry.probeHeldUntil !== undefined && entry.probeHeldUntil > Date.now()) {
          heldUnverifiable.push(entry)
          continue
        }
        // eslint-disable-next-line no-await-in-loop
        const verdict = await probeDeletedEntity(
          entry.deletedFrom,
          entry.firestoreId,
          entry.orgFirestoreId ?? '',
        )
        if (verdict === 'recreated') {
          // Dropped, and this is the safe direction the queue's docblock already names: a retained
          // Supabase row is still DETECTABLE by the set-diff and the census, while a row this
          // replay destroyed leaves no trace on either side.
          reportPendingSkipWithdrawn(entry)
          continue
        }
        if (verdict === 'unknown') {
          entry.probeHeldUntil = Date.now() + PENDING_SKIP_PROBE_HOLD_MS
          heldUnverifiable.push(entry)
          continue
        }
        // Guard 1, re-asserted across the probe. The probe is a server round-trip and this entry
        // left the queue before it started, so `purgePendingSkipsForDocument` could not reach it:
        // a live write for this document landing inside that window would otherwise be answered
        // by a read taken BEFORE it, and the replay would remove the row it just created.
        if (purgedDuringPendingSkipDrain?.has(entry.firestoreId)) {
          reportPendingSkipPurgedInFlight(entry)
          continue
        }
        // Answered: a later hold must start its own interval rather than inherit this one's.
        entry.probeHeldUntil = undefined
      }
      // A delete replay that finds NOTHING TO DELETE has succeeded (round-1 review finding 4).
      // `bestEffortDualWriteDelete`'s inner closure already swallows this for every site that uses it
      // (all `*.delete` labels since ATL-1195); a closure that calls the API bare would otherwise
      // throw here, classify as transient and be queued — burning ten attempts over three days against a row that never
      // existed, ending in a fingerprinted "permanent Supabase loss" that is false, while outbox
      // rule 2 parks that document's other entries behind it and each such entry occupies one of
      // the durable queue's 100 slots (whose overflow evicts the OLDEST DOCUMENT GROUP, i.e. some
      // other document's real write).
      //
      // It is not a hypothetical share of the population: the documents this queue replays are
      // exactly the ones deleted while the org's gate was denying, so "never mirrored" is the
      // EXPECTED outcome here rather than the exception.
      //
      // `isEntityAbsentFailure` is the ATL-1188 classifier, not `status === 404`: a dead-backend
      // 404 (ATL-902) and a route 404 (ATL-1194) mean the opposite thing — nothing ran — and both
      // must re-throw so they are classified, escalated and queued. Resolving as a success is also
      // what purges the document's queued outbox entries, so an older create cannot resurrect it.
      // The durable outbox's drain applies the same classifier to a replayed delete (ATL-1193).
      const replayFn = isDeleteLabel(entry.label)
        ? async () => {
            try {
              return await entry.fn()
            } catch (error) {
              if (!isEntityAbsentFailure(error)) throw error
              return undefined
            }
          }
        : entry.fn
      // Sequential, in enqueue order — same discipline as the outbox drain. Each replay
      // re-evaluates the gate; failures classify + queue durably through the normal path.
      // Per-entry try/race (delta review, finding 4): a synchronous throw must not drop the
      // rest of the batch, and a never-settling request must not wedge the drain flag for
      // the whole session (the capped replay is still a SINGLE execution — moving on never
      // re-runs it, so no double-apply).
      let capTimer: ReturnType<typeof setTimeout> | undefined
      let capped = false
      // Held so the capped branch can attach handlers to the SAME promise the race abandoned:
      // without that, a rejection arriving after the race has settled is swallowed by
      // `Promise.race` and a plumbing throw on a capped entry is never reported.
      const replay = dualWrite(entry.label, replayFn, entry.context, true, undefined)
      try {
        // eslint-disable-next-line no-await-in-loop
        capped =
          (await Promise.race([
            replay,
            new Promise<typeof REPLAY_CAP_REACHED>((resolveCap) => {
              capTimer = setTimeout(() => resolveCap(REPLAY_CAP_REACHED), REPLAY_ENTRY_CAP_MS)
            }),
          ])) === REPLAY_CAP_REACHED
      } catch (error) {
        // `dualWrite` never rejects for a failure of `fn` — it classifies and queues those. What
        // reaches here is a throw from the PLUMBING around it: `resolveGateDecision()` (which
        // touches the one unguarded `remoteConfig()` accessor on the uninformed branch),
        // `reportGateSkip`, or anything inside its catch. The entry has already been removed from
        // the queue at this point, so swallowing it silently is a lost write with no event at all
        // — and for the 4 entries that originated in `bestEffortDualWriteDelete`, its outer guard
        // and `reportDeleteWrapperThrow` are not on this path to catch it (round-1 review finding
        // 6). Report it under the same class rather than discard it; reporting must not be able to
        // break the drain, so it is itself guarded.
        reportPendingSkipReplayThrow(entry.label, error, entry.context)
      } finally {
        clearTimeout(capTimer)
      }
      if (capped) {
        // STOP the pass rather than start the next entry beside a replay that is still airborne
        // (round-2 review finding A). "Sequential, in enqueue order" has to mean that an entry
        // never LANDS before an earlier one, not merely that it starts later — which is the whole
        // basis for this queue relying on FIFO instead of the outbox's cross-label supersede rule.
        //
        // Breaking is not enough on its own, and an earlier revision that only broke was still
        // wrong: the drain is re-kicked by every successful live dual-write, so on a trading till
        // the next sale restarted it seconds later, beside the same airborne replay (round-3
        // review). `abandonedReplaysInFlight` is what actually holds the queue, and the abandoned
        // replay's own settlement releases it and re-kicks the drain — so the held entries do not
        // depend on a live write happening by. The drain flag itself is released by this
        // function's own `finally`, so nothing else is blocked meanwhile.
        abandonedReplaysInFlight += 1
        void replay
          .catch((error: unknown) => {
            // Reached only after the race abandoned this promise; before that the `catch` above
            // owns it. A plumbing throw is still a lost write and still gets its event.
            reportPendingSkipReplayThrow(entry.label, error, entry.context)
          })
          .finally(() => {
            abandonedReplaysInFlight -= 1
            if (pendingUninformedSkips.length && !pendingSkipDrainRunning) {
              void drainPendingUninformedSkips().catch(() => undefined)
            }
          })
        Sentry.addBreadcrumb({
          category: 'dual_write.gate',
          level: 'warning',
          message: `pending-skip replay exceeded ${REPLAY_ENTRY_CAP_MS}ms (${entry.label}) — drain held, ${pendingUninformedSkips.length} entr(y/ies) waiting`,
          data: { label: entry.label, firestoreId: entry.firestoreId, held: pendingUninformedSkips.length },
        })
        break
      }
    }
  } finally {
    // Back at the front, in their original order. A held entry is not lost while the session runs:
    // the next drain kick — any successful live dual-write — asks Firestore again.
    //
    // TWO RESIDUALS, stated at their true size rather than folded into the queue's existing one.
    // An earlier revision of this comment said a held entry is "lost exactly as every other
    // unreplayed entry in this in-memory queue is, which is the residual this queue has always
    // carried". That understates it, and the difference is a durability hole this ticket opened:
    //
    //  • a held entry never reaches the DURABLE outbox. Before ATL-1207 it would have replayed,
    //    and a replay that fails is persisted for seven days across sessions. It is now
    //    session-scoped for as long as the probe cannot answer — the narrow state "backend
    //    reachable, Firestore server read not". Accepted deliberately: the alternative is
    //    replaying on no evidence, which is the direction that destroys a live row, and every
    //    guard in this queue fails toward loss rather than destruction.
    //  • the restore can push the queue past MAX_PENDING_UNINFORMED_SKIPS, because
    //    `rememberUninformedSkip` enforced the cap mid-pass against a queue that excluded these
    //    entries. It re-converges on the next enqueue, which evicts oldest-first — i.e. these
    //    deletes first, the class this ticket exists to save. Same shape the queue's docblock
    //    already names for held entries sitting at the front.
    //
    // The restore comes BEFORE the flag is released, and the breadcrumb is guarded separately: a
    // reporter that can take down its caller here would take the queue down with it.
    // Guard 1 applies to the held entries too: while they sat in this array they were invisible
    // to `purgePendingSkipsForDocument`, so a live write that landed for one of their documents
    // mid-pass is applied here instead of being lost with the queue position.
    const purgedInPass = purgedDuringPendingSkipDrain
    purgedDuringPendingSkipDrain = null
    const restored = purgedInPass
      ? heldUnverifiable.filter((entry) => {
          if (!entry.firestoreId || !purgedInPass.has(entry.firestoreId)) return true
          reportPendingSkipPurgedInFlight(entry)
          return false
        })
      : heldUnverifiable
    if (restored.length > 0) {
      pendingUninformedSkips.unshift(...restored)
      try {
        Sentry.addBreadcrumb({
          category: 'dual_write.gate',
          level: 'warning',
          message: `pending-skip: ${restored.length} delete(s) held — Firestore could not say whether the entity is back`,
          data: { held: restored.length, labels: restored.map((e) => e.label).join(',') },
        })
      } catch {
        // Nothing left to do: reporting a failed report would recurse.
      }
    }
    pendingSkipDrainRunning = false
  }
}

/**
 * ATL-1445 stage 3, P5 — how long a WRITE cutover's restart waits for the pending-skip queue.
 *
 * Stage-3 §4, row "writes firestore → supabase": before the restart, "first run the pending-skip
 * drain". The queue is in memory, so a restart destroys it, and every entry is a write that is
 * already committed to Firestore and has not reached Supabase — which is about to become the
 * organization's MASTER. Losing it is a write the new master never sees.
 *
 * Capped all the same, for the reason every restart in this app is capped: a replay can hang for a
 * long time (`REPLAY_ENTRY_CAP_MS`), and the session it is protecting is writing Firestore-first on
 * an organization that has just become Supabase-master — a second master that grows with every
 * sale. Ten seconds covers a gate settle (`resolveGateDecision`, up to ~6 s) plus a few replays;
 * whatever is left is reported with its labels, never silently dropped.
 */
export const PENDING_SKIP_RESTART_DRAIN_CAP_MS = 10_000

export interface PendingSkipRestartDrainResult {
  /**
   *  - `empty`: nothing was queued.
   *  - `drained`: the queue emptied before the cap.
   *  - `gate-denied`: the gate (settled the way a live write settles it) still denies, so a replay
   *    would only re-queue; nothing was replayed.
   *  - `gate-error`: settling the gate THREW; nothing was replayed (`error` says what).
   *  - `not-delivered`: the gate allowed it and a pass ran, but entries remain (re-denied per entry,
   *    or probe-held); another pass would do the same, so the restart is not held for them.
   *  - `cap-expired`: the cap ran out with entries still queued or a pass still running.
   */
  outcome: 'empty' | 'drained' | 'gate-denied' | 'gate-error' | 'not-delivered' | 'cap-expired'
  /** The failure behind `gate-error`; null otherwise. */
  error: string | null
  /** Entries queued when the drain started. */
  before: number
  /**
   * Entries still queued when it returned — the writes the restart will lose. A pass still running
   * at the cap may hold more outside the queue (`settled` is then false).
   */
  remaining: number
  /** True when no pass and no abandoned replay was running at return (only then is `remaining` exact). */
  settled: boolean
  /** Their labels (a count per label), for the report. */
  remainingLabels: Record<string, number>
  gate: DualWriteGateReason | null
  waitedMs: number
}

/**
 * Drain the in-session pending-skip queue ahead of a restart, bounded by `capMs` (P5). Never
 * rejects. It replays through the queue's own drain (`drainPendingUninformedSkips`), so every guard
 * of that path holds: FIFO, one replay at a time, the recreate probe, the abandoned-replay hold.
 */
export async function drainPendingSkipsBeforeRestart({
  capMs = PENDING_SKIP_RESTART_DRAIN_CAP_MS,
  pollMs = 50,
  now = Date.now,
  sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
}: {
  capMs?: number
  pollMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
} = {}): Promise<PendingSkipRestartDrainResult> {
  const startedAt = now()
  const before = pendingUninformedSkips.length
  // An EMPTY array is not a drained queue while a pass runs: the pass `shift()`s each entry out
  // before replaying it, and holds the ones whose recreate probe went unanswered in its own array
  // until its `finally` puts them back (ATL-1207). A replay the cap abandoned is still airborne too.
  // Drained means all three are over.
  const pendingSkipsSettled = (): boolean =>
    pendingUninformedSkips.length === 0 && !pendingSkipDrainRunning && abandonedReplaysInFlight === 0
  const labels = (): Record<string, number> => {
    const counts: Record<string, number> = {}
    for (const entry of pendingUninformedSkips) counts[entry.label] = (counts[entry.label] ?? 0) + 1
    return counts
  }
  const result = (
    outcome: PendingSkipRestartDrainResult['outcome'],
    gate: DualWriteGateReason | null,
    error: string | null = null,
  ): PendingSkipRestartDrainResult => ({
    outcome,
    error,
    before,
    remaining: pendingUninformedSkips.length,
    settled: !pendingSkipDrainRunning && abandonedReplaysInFlight === 0,
    remainingLabels: labels(),
    gate,
    waitedMs: now() - startedAt,
  })
  if (before === 0 && pendingSkipsSettled()) return result('empty', null)

  const CAP = Symbol('pending-skip-restart-cap')
  let capTimer: ReturnType<typeof setTimeout> | undefined
  const cap = new Promise<typeof CAP>((resolve) => {
    capTimer = setTimeout(() => resolve(CAP), capMs)
  })
  try {
    let gate: DualWriteGateReason
    try {
      const settled = await Promise.race([resolveGateDecision(), cap])
      if (settled === CAP) return result('cap-expired', null)
      gate = settled
    } catch (error) {
      // `resolveGateDecision` is not expected to reject. If it does, nothing is replayed — and the
      // result says it was a FAILURE, never a policy deny it was not.
      return result('gate-error', null, error instanceof Error ? error.message : String(error))
    }
    if (gate !== 'allowed') return result('gate-denied', gate)

    // One pass of our own — plus ONE more when a pass (or an abandoned replay) was already running as
    // we started, because our kick was then a no-op. Never more: what a finished pass leaves behind
    // was re-denied or probe-held, re-running it every poll only re-holds it (and floods Sentry's
    // breadcrumbs), and the till keeps writing Firestore-first on a now Supabase-master organization
    // for as long as this waits. So that is reported at once as `not-delivered`.
    const passWasRunning = pendingSkipDrainRunning || abandonedReplaysInFlight > 0
    const maxKicks = passWasRunning ? 2 : 1
    let kicks = 0
    const kick = (): void => {
      kicks += 1
      void drainPendingUninformedSkips().catch(() => undefined)
    }
    kick()
    // Bounded twice, like `waitForWritesToDrain`: by the cap, and by an iteration count a frozen clock
    // cannot defeat.
    const maxIterations = Math.ceil(capMs / Math.max(1, pollMs)) + 2
    let iterations = 0
    while (iterations < maxIterations && now() - startedAt < capMs) {
      if (pendingSkipsSettled()) return result('drained', gate)
      const idle = !pendingSkipDrainRunning && abandonedReplaysInFlight === 0
      if (idle) {
        if (kicks >= maxKicks) return result('not-delivered', gate)
        kick()
      }
      iterations += 1
      // eslint-disable-next-line no-await-in-loop
      const tick = await Promise.race([sleep(pollMs), cap])
      if (tick === CAP) break
    }
    return pendingSkipsSettled() ? result('drained', gate) : result('cap-expired', gate)
  } finally {
    clearTimeout(capTimer)
  }
}

/** Raw allowlist string for telemetry (truncated) — lets a deny be diagnosed from the event. */
export function allowlistRawForTelemetry(): string {
  return policyString('dual_write_org_allowlist').slice(0, 200)
}

export function describeGateDecision(): DualWriteGateReason {
  if (!isDualWriteEnabledGlobally()) return 'global-kill-switch'
  const { all, ids, unknown } = parseOrgAllowlist()
  if (all) return 'allowed'
  if (unknown) return 'rc-unavailable'
  const org = getOrganization() as ({ id?: string; supabaseId?: string } & Record<string, unknown>) | null
  const firestoreId = org?.id
  const supabaseId = typeof org?.supabaseId === 'string' ? org.supabaseId : undefined
  return (firestoreId && ids.has(firestoreId)) || (supabaseId && ids.has(supabaseId))
    ? 'allowed'
    : 'not-in-allowlist'
}

/**
 * The gate decision PLUS whether it is an operator's decision or merely the absence of one
 * (ATL-983).
 *
 * `describeGateDecision()` alone cannot answer that: `not-in-allowlist` under a fresh policy is
 * an operator saying no, and the same string under a year-old cached policy — or the empty
 * allowlist of the ATL-911 broken channel — is the device knowing nothing. The write path has
 * always drawn this distinction (`resolveGateDecision`'s `uninformed` branch); the outbox drain
 * did not, and paused on both alike. This mirrors that test EXACTLY, synchronously: the drain
 * runs on a timer and must not trigger the bounded refetches `resolveGateDecision` performs.
 *
 * Callers that must not act on an uninformed deny should consult `informed`, not `decision`.
 */
export function describeGateAuthority(): { decision: DualWriteGateReason; informed: boolean } {
  const decision = describeGateDecision()
  if (decision === 'allowed') return { decision, informed: true }
  const uninformed = decision === 'rc-unavailable' || isRemoteConfigPolicyStale()
  return { decision, informed: !uninformed }
}

export function shouldDualWrite(): boolean {
  if (!isDualWriteEnabledGlobally()) {
    if (!loggedGateDecisions.has('__global_off__')) {
      loggedGateDecisions.add('__global_off__')
      // eslint-disable-next-line no-console
      console.log('[dualWrite] gate: globally DISABLED via Remote Config (dual_write_enabled=false)')
    }
    return false
  }
  const { all, ids } = parseOrgAllowlist()
  if (all) return true

  const org = getOrganization() as ({ id?: string; supabaseId?: string } & Record<string, unknown>) | null
  const firestoreId = org?.id
  const supabaseId = typeof org?.supabaseId === 'string' ? org.supabaseId : undefined
  const allowed = Boolean((firestoreId && ids.has(firestoreId)) || (supabaseId && ids.has(supabaseId)))

  const decisionKey = `${firestoreId || 'unknown'}:${allowed}`
  if (!loggedGateDecisions.has(decisionKey)) {
    loggedGateDecisions.add(decisionKey)
    // eslint-disable-next-line no-console
    console.log(
      `[dualWrite] gate: org ${firestoreId || '(none)'} ${allowed ? 'IN allowlist → dual-write ON' : 'NOT in allowlist → dual-write OFF'}`,
    )
  }
  return allowed
}

/** Optional structured context for a dual-write failure (drives reconciliation + debugging). */
export interface DualWriteContext {
  /** Firestore document id of the entity — the key the reconciliation job re-pushes by. */
  firestoreId?: string
  /** Supabase UUID, when known at call time. */
  supabaseId?: string
  /**
   * The org this write belongs to, when the caller knows it and the session does not (ATL-1077).
   *
   * `getOrganization()` is the normal source and is right for every write made by a till that is
   * already signed in. It is EMPTY exactly once — during signup, where the org being created is
   * the one that would populate the atom — and the outbox refuses an entry with no org
   * (`no-organization`, Rule 2), so a failed `organizations.create` was unqueueable and lost.
   * Declared rather than left to the index signature so the enqueue below can read it typed, and
   * so a future caller sees that this key is CONSUMED, not just logged.
   */
  orgFirestoreId?: string
  /**
   * ATL-1207 — for a DELETE, where in Firestore the entity lived, so a replay of this write can
   * check whether it is back before firing.
   *
   * Stated by the call site because only the call site knows: the label names an API module, not a
   * Firestore location, and the constants families keep their entities as ITEMS of a document
   * (`constants/wallets.data[]`) rather than as documents. Both replay queues treat it as this
   * write's precondition — "valid only while that entity is still gone" — and refuse to replay
   * over a recreate, which is the only thing at this seam that can see another device. Wired
   * exactly where a document id can be REUSED; see `deletedEntityLocation`.
   */
  deletedFrom?: DeletedEntityLocation
  /** Any extra structured detail (storeId, counts, …). */
  [key: string]: unknown
  /**
   * ATL-1445 stage 2c — the caller DECLARES that this write may be replayed later even though its
   * label is not create-class. The gate's in-session queue replays only create-class skips, because
   * an absolute UPDATE replayed late clobbers a newer one. A keyed DELTA is the exception: it
   * commutes, and a replay of an already-applied key is a server no-op, while NOT replaying it
   * loses it for good (nothing later heals a delta). Declared here, per call site, so replay-safety
   * never depends on how a label happens to be spelled.
   */
  replaySafe?: boolean
  /**
   * ATL-1503 — this write carries a PRECONDITION (the document state it was made from), so it must
   * never overtake writes of the same document that are still queued: it would reach a state it was
   * not made from and be refused, and the queued ones would then replay over it. When the outbox
   * holds entries for `firestoreId`, the write is NOT sent: this returns the request exactly as it
   * would be sent (`captureWriteRequest`), and it is queued behind them. Requires `firestoreId`.
   * See `isPreconditionedRequest` in dualWriteOutbox.ts.
   */
  queueBehindDocument?: () => Promise<ReplayableRequestConfig>
}

/** Split a `module.op` label into its parts (op may itself contain dots; keep them on the op). */
function splitLabel(label: string): { module: string; op: string } {
  const i = label.indexOf('.')
  if (i === -1) return { module: label, op: '' }
  return { module: label.slice(0, i), op: label.slice(i + 1) }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  try {
    return String(error)
  } catch {
    return 'unknown error'
  }
}

/** Cap on serialized issue lists — keeps the console line + Sentry context bounded. */
const MAX_ISSUES = 20

/**
 * Pull the structured validation detail off a thrown API error (ATL-566).
 *
 * A backend 4xx (`ApiRequestError`) carries the normalized error body on `.error` —
 * `message` holds the real constraint text (e.g. "price must be a number"), unwrapped
 * from the AllExceptionsFilter envelope by `normalizeApiErrorBody` in the api-client,
 * plus optional structured `validationErrors[]`. A client-side Zod rejection
 * (`ApiValidationError`) carries the issues on `.errors`. But `Sentry.captureException`
 * only serializes an Error's `.message`/`.name`, so that detail was being dropped —
 * every dual-write 400 surfaced as a bare "status code 400" with no field, making the
 * real cause (residual payload/DTO mismatches, ATL-482/541) invisible. Duck-typed (no
 * api-client import / circular-dep risk). The rejected `value` is intentionally OMITTED
 * to keep customer data out of Sentry — the field name + message is enough to fix the
 * payload.
 */
function apiErrorDetail(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== 'object') return undefined
  const e = error as Record<string, unknown>
  const detail: Record<string, unknown> = {}

  if (typeof e.statusCode === 'number') detail.statusCode = e.statusCode

  // ApiRequestError: normalized backend error body lives on `.error`.
  const body = e.error
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>
    if (typeof b.message === 'string') detail.serverMessage = b.message
    if (Array.isArray(b.validationErrors)) {
      detail.validationErrors = b.validationErrors.slice(0, MAX_ISSUES).map((v) => {
        const ve = (v ?? {}) as Record<string, unknown>
        return { field: ve.field, message: ve.message } // value omitted (PII)
      })
      if (b.validationErrors.length > MAX_ISSUES) {
        detail.validationErrorsTruncated = b.validationErrors.length - MAX_ISSUES
      }
    }
  }

  // ApiValidationError: client-side Zod issues live on `.errors` (+ `.type`/`.path`).
  // Gate on the error NAME so generic `.errors` carriers (e.g. AggregateError) don't
  // get mislabeled as Zod issues.
  if ((error as Error).name === 'ApiValidationError' && Array.isArray(e.errors)) {
    detail.zodIssues = e.errors.slice(0, MAX_ISSUES).map((v) => {
      const ze = (v ?? {}) as Record<string, unknown>
      return {
        path: Array.isArray(ze.path) ? ze.path.join('.') : ze.path,
        message: ze.message,
      }
    })
    if (e.errors.length > MAX_ISSUES) {
      detail.zodIssuesTruncated = e.errors.length - MAX_ISSUES
    }
    if (typeof e.type === 'string') detail.validationType = e.type
    if (typeof e.path === 'string') detail.validationPath = e.path
  }

  return Object.keys(detail).length > 0 ? detail : undefined
}

/**
 * Classify a caught dual-write failure as a DETERMINISTIC DEFECT vs a transient/expected failure
 * (ATL-534 / gate-honesty in the write path).
 *
 * The whole dual-write validation family (ATL-534/538/539/540/541 + the live `stores` front:
 * timezone / wiponCredentials) shares one deeper bug: a payload that fails validation is caught
 * here and reported at `level: warning`, grouped by module/op — indistinguishable from the benign,
 * self-healing noise this helper was built to tolerate (backend briefly down, referenced entity not
 * migrated yet → 401 "User record not found", 5xx). So a real, DETERMINISTIC shape bug — one that
 * will NEVER self-heal on retry and silently drops the Supabase row on EVERY call — hides inside the
 * transient-failure budget and never surfaces. That is the silent no-op: the invalid shape vanishes
 * instead of being fixed.
 *
 * A failure is a deterministic defect when the payload is rejected for its SHAPE, not the environment:
 *  - a client-side request-schema rejection (`ApiValidationError`, `type: 'request'`) — the body never
 *    even left the device, so it is 100% our payload bug, and
 *  - a backend `400 Bad Request` (`ApiRequestError`, statusCode 400) — the ValidationPipe rejected the
 *    shape; deterministic given the same payload.
 * Everything else (401/404, 5xx, network, timeouts, response-validation) is transient or
 * migration-state noise and stays a `warning`.
 *
 * ATL-1487 — 409/422 used to be in that list. They are not noise: the backend evaluated the write
 * and refused it (a foreign unique constraint, a money leg it will not touch, a refund over the
 * sale's total), so the write did NOT land and never will. They are not defects either — the shape
 * was fine, it was the STATE that refused it — so, like 403 and the dead backend, they get their
 * own class: `isRefusedWriteFailure` (dualWriteRefusal.ts), loud, fingerprinted by the backend's
 * refusal `code`. Queueing is unaffected: `isPermanentReplayFailure` already keeps them out of the
 * outbox, because retrying cannot change the answer.
 *
 * ATL-1137 — 403 used to be in that list and is not a defect either, but it was never noise.
 * Note what this function's name promises and what its RESULT is used for: `isDefect` decides both
 * how loudly to report AND whether to queue (`if (!isDefect && !isPermanentReplayFailure(error))`
 * below). Those are different questions, and 403 is the case that separates them — deterministic
 * for this session, so it must be loud; healable out of band by a role grant or a guard fix, so it
 * must stay queued. It therefore gets its OWN class rather than being forced into this one, the
 * same way ATL-902 gave the dead host its own: see `isForbiddenFailure`. Widening `isDefect` to
 * cover it, which is what ATL-1137 originally proposed, would have made it loud by making it
 * unqueueable — buying visibility with the write.
 *
 * Deterministic defects are escalated (level `error`, `dual_write_defect: true`) and fingerprinted by
 * the failing FIELD path(s), so each distinct shape bug is its own alertable issue and cannot be
 * absorbed into the transient bucket — the invalid shape SURFACES instead of disappearing.
 */
export function classifyDualWriteDefect(
  error: unknown,
  apiDetail: Record<string, unknown> | undefined,
): { isDefect: boolean; fields: string[] } {
  const name = (error as { name?: unknown })?.name
  const isClientRequestRejection =
    name === 'ApiValidationError' &&
    (error as { type?: unknown }).type === 'request'
  const isBackendBadRequest =
    name === 'ApiRequestError' &&
    (error as { statusCode?: unknown }).statusCode === 400
  const isDefect = Boolean(isClientRequestRejection || isBackendBadRequest)

  // The failing field paths — from client Zod issues or backend validationErrors — become part of the
  // fingerprint so `stores.create timezone` and `stores.create name` are DIFFERENT issues, and a fix
  // to one is visible as that fingerprint going quiet. Bounded + sorted for a stable fingerprint.
  const fields = new Set<string>()
  if (apiDetail) {
    const zodIssues = apiDetail.zodIssues
    if (Array.isArray(zodIssues)) {
      for (const z of zodIssues) {
        const p = (z as { path?: unknown })?.path
        if (typeof p === 'string' && p) fields.add(p)
      }
    }
    const validationErrors = apiDetail.validationErrors
    if (Array.isArray(validationErrors)) {
      for (const v of validationErrors) {
        const f = (v as { field?: unknown })?.field
        if (typeof f === 'string' && f) fields.add(f)
      }
    }
  }
  return {
    isDefect,
    fields: Array.from(fields).sort().slice(0, 5),
  }
}

/**
 * ATL-775 — make a SKIPPED dual-write leave a trace.
 *
 * Volume discipline, because this runs on the hottest write path in the product: a skip is recorded
 * ONCE per (label, reason) per session as a Sentry breadcrumb, so a later failure report carries the
 * gate history without adding an event per sale. Only `rc-unavailable` raises an actual Sentry
 * message, and only once per session per label — that is the case where we denied a write without
 * knowing the policy, i.e. a permanent silent loss, and it is the one worth waking someone for.
 * `global-kill-switch` and `not-in-allowlist` are INFORMED decisions and stay quiet by design;
 * escalating them would recreate exactly the failure-rate pollution the original gate avoided.
 */
const reportedGateSkips = new Set<string>()

/**
 * TEST-ONLY: clear the per-session skip-dedupe. The dedupe is deliberately session-scoped in
 * production (a busy store must not emit one Sentry event per sale during a cold start), which
 * means it leaks across tests unless they can reset it. Exported rather than reached into via
 * module internals so the dedupe stays private.
 */
export function resetGateSkipReportingForTests(): void {
  reportedGateSkips.clear()
}

export function reportGateSkip(
  label: string,
  reason: DualWriteGateReason,
  context?: DualWriteContext,
): void {
  const key = `${label}:${reason}`
  const firstForThisKey = !reportedGateSkips.has(key)
  if (firstForThisKey) reportedGateSkips.add(key)

  // Both reasons where the write is lost WITHOUT a trustworthy policy behind the deny:
  // `rc-unavailable` (no policy at all) and `rc-stale` (a policy the device could not refresh
  // within the trust bound — ATL-786). Informed denies (kill-switch, fresh not-in-allowlist)
  // stay quiet by design.
  const uninformedLoss = reason === 'rc-unavailable' || reason === 'rc-stale'

  Sentry.addBreadcrumb({
    category: 'dual_write.gate',
    level: uninformedLoss ? 'warning' : 'info',
    message: `dual-write SKIPPED: ${label} (${reason})`,
    data: { label, reason, ...(context || {}) },
  })

  if (!uninformedLoss || !firstForThisKey) return

  // The raw allowlist + fetch age make the stale-cohort diagnosis possible from the event
  // alone (ATL-786 ask 1) — without them a deny is indistinguishable from current policy.
  const rcFetchedAt = getRemoteConfigFetchTimeMillis()
  const rcDetail = {
    allowlistRaw: allowlistRawForTelemetry(),
    rcFetchAgeMs: rcFetchedAt > 0 ? Date.now() - rcFetchedAt : -1,
    // ATL-911: WHICH source answered. `remote-config` with an empty allowlist and a recent
    // rcFetchAgeMs is the broken-SDK signature; `none` means the backend fallback could not be
    // reached either. Without this field a skip cannot be told apart from a fallback that worked.
    policySource: describePolicySource(),
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[dualWrite] ${label} SKIPPED without a trustworthy Remote Config policy (${reason}) — ` +
      `the write is lost in Supabase and Firestore already committed`,
    JSON.stringify({ label, reason, ...rcDetail, ...(context || {}) }),
  )

  Sentry.captureMessage(
    reason === 'rc-stale'
      ? `dual-write skipped on a stale Remote Config policy (${label})`
      : `dual-write skipped with no Remote Config policy (${label})`,
    {
      level: 'warning',
      tags: {
        // NOTE: Sentry tag values are case-sensitive and booleans serialise CAPITALISED, so search
        // for `dual_write_gate_skipped:True`, not `:true` (docs/technical/external-access.md:16).
        dual_write_gate_skipped: true,
        dual_write_gate_reason: reason,
        dual_write: label,
      },
      fingerprint: ['dual-write-gate-skipped', reason, label],
      contexts: { dual_write_gate: { label, reason, ...rcDetail, ...(context || {}) } },
    },
  )
}

/**
 * ATL-1503 — how long a supply payment save may wait on the outbox before it gives up queueing behind
 * and sends live. The same 3s the failure path allows its enqueue (see `bestEffortDualWrite`): the
 * queue sits behind a cross-tab Web Lock with no timeout of its own, and a frozen tab holding it must
 * never hang a save.
 */
export const QUEUE_BEHIND_WAIT_MS = 3000

async function withinCap<T>(work: Promise<T>): Promise<{ settled: true; value: T } | { settled: false }> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      work.then((value) => ({ settled: true as const, value })),
      new Promise<{ settled: false }>((resolve) => {
        timer = setTimeout(() => resolve({ settled: false }), QUEUE_BEHIND_WAIT_MS)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

/** What a preconditioned write sent live past its document's queue can meet (review 4). */
const QUEUE_BEHIND_LIVE_OUTCOMES =
  'what a live send past the queue can meet — refused: a stale unpay/edit (409 supply_payment_precondition_failed); retried after its pay: an unpay/edit that overtook the payment it names (503 supply_payment_not_yet_applied); NOT caught: an unpay of a LEG-LESS payment that overtook it (no_change — Supabase can end paid while Firestore is unpaid)'

function reportQueueBehindNotDone(
  label: string,
  reason: 'probe-timeout' | 'enqueue-timeout' | 'enqueue-refused',
  detail: { firestoreId: string; orgFirestoreId?: string },
): void {
  // eslint-disable-next-line no-console
  console.error(
    `[dualWrite] ${label} could not be queued behind its document's queued writes (${reason}) — sent live, ` +
      `so it may reach Supabase before older writes of the document and the outcome CAN DIVERGE: ` +
      `${QUEUE_BEHIND_LIVE_OUTCOMES}`,
    JSON.stringify({ label, reason, ...detail }),
  )
  Sentry.captureMessage(`dual-write ${label} could not be queued behind its document's queued writes (${reason})`, {
    level: 'error',
    tags: { dual_write: label, dual_write_queue_behind_failed: true, dual_write_queue_behind_reason: reason },
    fingerprint: ['dual-write-queue-behind-failed', label, reason],
    contexts: { dual_write: { label, reason, ...detail, liveOutcomes: QUEUE_BEHIND_LIVE_OUTCOMES } },
  })
}

/**
 * ATL-1503 — queue a preconditioned write behind its document's queued writes, instead of sending it.
 * True when it was queued (the caller must not send it); false when the document has nothing queued,
 * or it could not be queued in time — then it is SENT LIVE, past the queue, and every way of not
 * queueing is reported loudly, because the outcome can diverge. The precondition decides what the live
 * write meets:
 *  - a stale unpay/edit (made from a payment that is no longer current): refused, 409, surfaced;
 *  - an unpay/edit that overtook the payment it names (its pay is still queued behind it here, or on
 *    another till): refused RETRYABLY (503 `supply_payment_not_yet_applied`) — the failure path queues
 *    it, behind the pay, and it replays in order;
 *  - an unpay of a LEG-LESS payment that overtook that pay: NOT caught — it names no leg, so it looks
 *    exactly like an unpay that already happened (no_change), and the pay replayed after it leaves
 *    Supabase paid while Firestore is unpaid. No money leg is involved (a leg-less payment books none);
 *    the supplies set-diff and /compare see the flag.
 *
 * Both awaits are capped (`QUEUE_BEHIND_WAIT_MS`). An enqueue that finishes AFTER its cap is withdrawn
 * under the queue lock (`stillWanted`), so the live write is not also replayed later. The one residual
 * — the lock granted and the entry written in the same instant the cap fires — delivers the write
 * twice, and the second delivery meets the state the first left (a replay no-op, or a 409).
 */
async function queuedBehindDocument(label: string, context: DualWriteContext): Promise<boolean> {
  const firestoreId = context.firestoreId as string
  const capture = context.queueBehindDocument as () => Promise<ReplayableRequestConfig>
  const orgFirestoreId =
    typeof context.orgFirestoreId === 'string' && context.orgFirestoreId.length > 0
      ? context.orgFirestoreId
      : getOrganization()?.id
  const probe = await withinCap(outboxHoldsDocument(firestoreId, orgFirestoreId).catch(() => false))
  if (!probe.settled) {
    reportQueueBehindNotDone(label, 'probe-timeout', { firestoreId, orgFirestoreId })
    return false
  }
  if (!probe.value) return false
  let request: ReplayableRequestConfig
  try {
    request = await capture()
  } catch {
    // It never reached a transport (a client-side refusal): the live call fails the same way and is
    // classified and reported there.
    return false
  }
  let wanted = true
  const enqueued = await withinCap(
    enqueueBehindQueuedEntries(label, request, { firestoreId, orgFirestoreId }, { stillWanted: () => wanted }).catch(
      () => false,
    ),
  )
  if (!enqueued.settled) {
    wanted = false
    reportQueueBehindNotDone(label, 'enqueue-timeout', { firestoreId, orgFirestoreId })
    return false
  }
  if (!enqueued.value) {
    reportQueueBehindNotDone(label, 'enqueue-refused', { firestoreId, orgFirestoreId })
    return false
  }
  Sentry.addBreadcrumb({
    category: 'dual_write.outbox',
    level: 'info',
    message: `outbox: ${label} queued behind earlier writes of ${firestoreId} (ATL-1503)`,
    data: { label, firestoreId },
  })
  void drainDualWriteOutbox('enqueue').catch(() => undefined)
  return true
}

/**
 * First `bestEffortDualWrite` call of the session kicks a drain of the persisted outbox
 * (ATL-782): the queue from the PREVIOUS session replays as soon as the app is being used
 * for writes again — which, on an intermittently-used POS, is the earliest moment that
 * both auth and network are known-good.
 */
let launchDrainKicked = false

// ============================================================================
// ATL-1445 stage 3, P8 — a LIVE mirror refused by backend B1 (fires only for a Supabase-master org)
// ============================================================================

/** How a live refused mirror ended (`global/Write/mirrorRefusalConversion.ts`). */
type LiveMirrorConversion = 'queued' | 'unresolved' | 'not-applicable'

/** What the live path needs from the conversion module, required lazily. */
interface LiveMirrorRefusalConversion {
  masterRefusalOf: (error: unknown) => { organizationFirestoreId: string } | null
  convertRefusedLiveMirror: (
    label: string,
    error: unknown,
    context: { orgFirestoreId?: string; firestoreId?: string },
  ) => Promise<LiveMirrorConversion>
}

/**
 * Lazily: the conversion reaches outbox v2, which is never loaded for a session that has not seen a
 * 409. A module that fails to load is `null`, and today's handling applies.
 */
function liveMirrorRefusalConversion(): LiveMirrorRefusalConversion | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('../Write/mirrorRefusalConversion') as LiveMirrorRefusalConversion
  } catch {
    return null
  }
}

/**
 * B1's refusal carried by a live failure, or null. Synchronous and free of I/O: a non-409 never loads
 * the module, and a 409 without B1's body — every 409 today's backend sends — is null.
 */
function liveMasterRefusalOf(error: unknown): { organizationFirestoreId: string } | null {
  if (failureStatus(error) !== 409) return null
  return liveMirrorRefusalConversion()?.masterRefusalOf(error) ?? null
}

/** The conversion's own bound, like the live enqueue's: a wedged store must never hang checkout. */
const LIVE_CONVERSION_WAIT_MS = 3000

/**
 * Hand a live refused mirror to the conversion, bounded. `queued` → it is durable in the v1 outbox and a
 * drain converting it into outbox v2 was kicked. `unresolved` when the bound expired, the module is
 * unavailable or it threw: the caller then reports exactly as it does today (a refusal, loud), and a hand-
 * off still running finishes on its own (the over-report is the safe direction, as for the enqueue race).
 */
async function convertLiveRefusedMirror(
  label: string,
  error: unknown,
  context: { orgFirestoreId?: string; firestoreId?: string },
): Promise<LiveMirrorConversion> {
  const conversion = liveMirrorRefusalConversion()
  if (!conversion) return 'unresolved'
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      conversion.convertRefusedLiveMirror(label, error, context),
      new Promise<LiveMirrorConversion>((resolve) => {
        timer = setTimeout(() => resolve('unresolved'), LIVE_CONVERSION_WAIT_MS)
      }),
    ])
  } catch {
    return 'unresolved'
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

export async function bestEffortDualWrite<T>(
  label: string,
  fn: () => Promise<T>,
  context?: DualWriteContext,
  onOutcome?: DualWriteOutcomeSink,
): Promise<T | null> {
  return dualWrite(label, fn, context, false, onOutcome)
}

/**
 * ATL-1644 — the class of a failed dual-write, in the precedence the Sentry fingerprint uses, so it
 * names the issue Sentry groups the failure under. It does NOT say whether the write was queued:
 * the outbox applies its own permanence rule (an untyped 4xx, for one, classifies `transient` here
 * yet is never queued) — read `queued` for that.
 */
export type DualWriteFailureClass =
  | 'dead-backend'
  | 'route-not-found'
  | 'forbidden'
  | 'refused'
  | 'defect'
  | 'migration-paused'
  | 'transient'

/**
 * ATL-1644 — which terminal path a live dual-write took. `bestEffortDualWrite` returns `null` for
 * every case but `landed`, which made "did this sale reach Supabase, and if not, why" unanswerable
 * from telemetry for a sale whose POST never reached the server.
 *   landed                     the request succeeded.
 *   gate-denied                never sent: the policy gate refused it. `pendingSkipQueued` says the
 *                              write was remembered for in-session replay (uninformed denies only).
 *                              That queue is IN MEMORY: an app kill, a later eviction or a purge still
 *                              loses it, so `true` is "may replay this session", never "safe".
 *   queued-behind-document     never sent now: parked behind an older queued write of its document.
 *   converted-to-master-write  refused as a mirror of a Supabase-master org; durable in the outbox.
 *   failed                     sent and failed; `queued` says it is durable in the outbox — a failed
 *                              write with `queued: false` is LOST unless something else repairs it.
 *                              One exception, in the safe direction: when the enqueue outlives its 3s
 *                              race it may still persist, so `false` can over-report a loss (the same
 *                              rule as the `dual_write_outbox_queued` tag). `true` is never optimistic
 *                              about the enqueue (the entry was persisted), but it is not delivery: the
 *                              outbox can still drop the entry later (attempts, age, overflow, refusal).
 */
export type DualWriteOutcome =
  | { kind: 'landed' }
  | {
      kind: 'gate-denied'
      gate: Exclude<DualWriteGateReason, 'allowed'>
      pendingSkipQueued: boolean
    }
  | { kind: 'queued-behind-document' }
  | { kind: 'converted-to-master-write' }
  | { kind: 'failed'; queued: boolean; failureClass: DualWriteFailureClass }

/**
 * ATL-1644 — receives the terminal path of ONE live `bestEffortDualWrite`, synchronously, at the
 * moment the write decided it, and never on a pending-skip replay. A sink is an observer: it does not
 * change what is written, queued or reported, and it adds no await, so the live path keeps master's
 * timing tick for tick (pinned by `dualWrite.p8LiveDifferential.test.js`). A sink that throws is a
 * defect in the observer, never in the write: `reportOutcome` reports it to Sentry under its own
 * fingerprint and the write's result is returned unchanged — never re-queued, never a rejection.
 * By the time any outcome is known the caller's Firestore commit has happened, so a rejection here
 * would turn a saved sale into a checkout error.
 */
export type DualWriteOutcomeSink = (outcome: DualWriteOutcome) => void

/**
 * ATL-1644 — hand `outcome` to the sink. Synchronous (no await, so the live path's microtask count
 * is master's) and total: whatever the sink throws is reported and swallowed here, because an
 * observer must not be able to change what the write did.
 */
function reportOutcome(onOutcome: DualWriteOutcomeSink | undefined, label: string, outcome: DualWriteOutcome): void {
  if (!onOutcome) return
  try {
    onOutcome(outcome)
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: { dual_write_outcome_sink_threw: true, dual_write_outcome: outcome.kind },
      fingerprint: ['supabase-dual-write-outcome-sink-threw', label],
      contexts: { dual_write_outcome: { label, outcome } },
    })
  }
}

/**
 * `bestEffortDualWrite`, plus the one thing the drain needs to say and no caller does: whether
 * this execution is a LIVE write or the pending-skip drain flushing an older one (ATL-1192,
 * round-1 review finding 1).
 *
 * The distinction is load-bearing, not cosmetic. Guard 1 (`purgePendingSkipsForDocument`) reads a
 * success as "this document exists NOW, so a delete queued before it is stale". That is true of a
 * live write and FALSE of a replay: a replayed entry is by construction OLDER than everything
 * queued after it, so letting a replayed create purge the delete that followed it in the same
 * outage deletes the delete — Supabase keeps a row the customer removed, with nothing queued
 * anywhere to remove it. Measured on the real queue before this parameter existed: a
 * `products.create` replay whose response carries `{ id, firestoreId }` (which every backend
 * create response does, by repo convention) purged the queued `products.delete` and the drain
 * then found an empty queue. That is this ticket's own loss, re-created by its own guard, and
 * worse than the original: before the change neither write replayed, so Supabase never gained
 * the row in the first place.
 */
async function dualWrite<T>(
  label: string,
  fn: () => Promise<T>,
  context: DualWriteContext | undefined,
  isPendingSkipReplay: boolean,
  onOutcome: DualWriteOutcomeSink | undefined,
): Promise<T | null> {
  // Gate (ATL-484/466 canary scoping, ATL-775 cold-start settle, ATL-786 stale-policy refusal):
  // skip when dual-write is globally off or the org isn't on the canary allowlist. An INFORMED
  // no-op returns null (callers already tolerate null) with no Sentry event; an uninformed one
  // (no policy / stale policy) is escalated inside reportGateSkip AND remembered for in-session
  // replay once the policy heals.
  const gate = await resolveGateDecision()
  if (gate !== 'allowed') {
    let pendingSkipQueued = false
    // Creates, and deletes that state which document they are for — see the queue docblock.
    if (gate === 'rc-stale' || gate === 'rc-unavailable') {
      if (isReplaySafeCreateOp(splitLabel(label).op) || context?.replaySafe === true) {
        rememberUninformedSkip({ label, fn, context })
        pendingSkipQueued = true
      } else if (isDeleteLabel(label)) {
        // `isDeleteLabel` is the outbox's, not a second spelling of it: the two queues must agree
        // on what a delete IS, and its label shapes (`delete`, `deleteX`) came from that census.
        // It deliberately does not match `users.softDelete`, which is an update to a flag.
        //
        // The identity must be STATED by the caller. `deriveDocumentId`'s URL-scan fallback is not
        // consulted and cannot be — there is no request to scan, because a gate-denied write never
        // reached the transport, which is the whole reason this queue exists.
        const firestoreId = context?.firestoreId
        if (typeof firestoreId === 'string' && firestoreId.length > 0) {
          // ATL-1207. The org is read HERE, not at replay time: it is the org that owned the
          // delete, and a session that switched orgs in between must not have this entry's
          // precondition evaluated against the org it happens to be in now. Resolved with the
          // same rule the outbox path below uses — an explicit caller org wins, but only when it
          // is actually a non-empty string.
          const skipOrgFirestoreId =
            typeof context?.orgFirestoreId === 'string' && context.orgFirestoreId.length > 0
              ? context.orgFirestoreId
              : getOrganization()?.id
          rememberUninformedSkip({
            label,
            fn,
            context,
            firestoreId,
            // …and the location is carried ONLY when an org was resolved. `probeDeletedEntity`
            // refuses to read for an entry whose org it cannot match, so a location with no org
            // is a precondition that can never be evaluated: the entry would be held on every
            // pass and lost when the session ends — strictly worse than the pre-ATL-1207
            // behaviour of replaying it. (The durable outbox enforces the same rule by refusing
            // to queue at all without an org, `no-organization`.)
            deletedFrom: skipOrgFirestoreId ? context?.deletedFrom : undefined,
            orgFirestoreId: skipOrgFirestoreId,
          })
          pendingSkipQueued = true
        } else {
          reportUnqueueableUninformedSkip(label, context)
        }
      }
    }
    reportGateSkip(label, gate, context)
    reportOutcome(onOutcome, label, { kind: 'gate-denied', gate, pendingSkipQueued })
    return null
  }

  if (!launchDrainKicked) {
    launchDrainKicked = true
    // Fire-and-forget: draining last session's queue must never delay a live sale.
    void drainDualWriteOutbox('launch').catch(() => undefined)
  }

  // ATL-1503 — a preconditioned write never overtakes the queued writes of its document.
  if (context?.queueBehindDocument && context.firestoreId && (await queuedBehindDocument(label, context))) {
    reportOutcome(onOutcome, label, { kind: 'queued-behind-document' })
    return null
  }

  let landedResult: T
  try {
    const result = await fn()
    // A live success supersedes queued older writes for this document (ATL-782 ordering
    // rules — a live DELETE additionally purges the document across all labels) and is a
    // good moment to opportunistically drain: network is demonstrably up. The document id
    // is taken from the API RESPONSE when the caller passed no context — backend responses
    // carry `{ id, firestoreId, supabaseId }` by repo convention — so the purge is active on
    // every dual-write path, not only the ~10% that pass explicit context.
    const resultRecord = result as { firestoreId?: unknown; id?: unknown } | null | undefined
    const responseIds = [resultRecord?.firestoreId, resultRecord?.id].filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    )
    const successFirestoreId = context?.firestoreId ?? responseIds[0]
    void noteDualWriteSuccess(label, {
      firestoreId: successFirestoreId,
      // Endpoints can key the URL on one id and the document on another; purging on the
      // union closes that gap (saleSettings-style mismatches).
      alternateIds: responseIds,
      // A REPLAYED delete must not purge the document across all labels in the durable queue —
      // it is an old statement, and an entry queued after it is newer. Same argument as the
      // pending-skip purge below; this is the other half of it (round-2 review finding B).
      fromReplay: isPendingSkipReplay,
    })
      .then(() => drainDualWriteOutbox('live-success'))
      .catch(() => undefined)
    // A queued DELETE for a document this LIVE write just proved to exist must not replay
    // (ATL-1192). Synchronous, and BEFORE the drain below, so it cannot lose the race to the
    // replay it exists to prevent. Same id union as the outbox purge above, for the same reason.
    // Skipped for a replay: see `dualWrite`'s docblock — a replay is an OLD statement, and
    // treating it as evidence of the document's current state deletes the newer delete.
    if (!isPendingSkipReplay) purgePendingSkipsForDocument([successFirestoreId, ...responseIds])
    // Policy is demonstrably good (this write just landed) — replay skips from earlier uninformed
    // denies. AFTER the live write, not before (delta review, finding 1): draining pre-write would
    // let a queued older create race the live one.
    if (pendingUninformedSkips.length && !pendingSkipDrainRunning) {
      void drainPendingUninformedSkips().catch(() => undefined)
    }
    landedResult = result
  } catch (error) {
    const { module, op } = splitLabel(label)
    const apiDetail = apiErrorDetail(error)
    // A deterministic shape defect (client request-schema rejection or backend 400) will never
    // self-heal and drops the Supabase row on every call — it must SURFACE, not vanish into the
    // transient-failure warning budget (ATL-534 silent no-op; ATL-664 gate-honesty in the write path).
    const { isDefect, fields } = classifyDualWriteDefect(error, apiDetail)
    // ATL-902: this bundle is talking to a host that no longer exists, so EVERY dual-write it
    // makes is one-sided — including deletes, which leave Supabase holding rows the customer
    // removed. It is not a defect (the payload is fine) and not transient (nothing about the
    // device will change on its own), so it fits neither existing bucket and was reported at
    // `warning` inside the benign-noise budget for two months. Its own level, tag and
    // fingerprint make it separable and alertable.
    const isDeadBackend = isDeadBackendFailure(error)
    // ATL-1137: a 403 is a guard refusing this caller's ROLE. Like the dead backend above it is
    // neither a payload defect (the body was never evaluated) nor transient (nothing about the
    // device or the network changes it), so it fitted neither bucket and was reported at
    // `warning` with `dual_write_defect: False` — a tag that asserted the opposite of the truth
    // while the write was lost below alert threshold. Its own level, tag and fingerprint make it
    // separable and alertable. It stays QUEUED: see `isForbiddenFailure`.
    const isForbidden = isForbiddenFailure(error)
    // ATL-1194: nothing our backend routes to answered — Nest's fallback handler did. Given its
    // own tag for the same reason as the two above, and for one more: the drain now HOLDS this
    // class for up to MAX_AGE_MS without consuming an attempt, and a class worth holding entries
    // for a week is a class someone has to be able to count. Without a tag it fuses into the
    // shared per-op transient bucket alongside 5xx and timeouts, and "how many devices are
    // stranded on a route 404, and did the hold help" is unanswerable from the error stream —
    // which is the exact invisibility ATL-902 sat in for two months.
    const isRouteNotFound = isRouteNotFoundFailure(error)
    // ATL-1487: the backend evaluated this write and REFUSED it (409/422) — a foreign unique
    // constraint, a money leg it will not touch, a refund over the sale total. The write did not
    // land, and it is not queued (retrying cannot change the answer), so this report is the only
    // trace the loss leaves. It was a `warning` in the shared per-op transient bucket, i.e. a
    // permanent loss with neither a retry nor an alert. Its own level, tag, fingerprint (keyed by
    // the backend's refusal code) and PostHog event make it countable and alertable.
    const isRefused = isRefusedWriteFailure(error)
    const refusal = isRefused ? refusalCode(error) : undefined
    // ATL-1805: the organization is being re-migrated and its dual-write is paused (503
    // `migration_paused` / `books_write_paused`) — nothing executed, the write is queued and the
    // drain holds it without spending an attempt until the pause lifts. It is the pause working,
    // not a 5xx: its own class, tag and fingerprint keep an hours-long run from flooding the shared
    // per-op transient bucket that real server faults are read from.
    const isMigrationPaused = isMigrationPausedFailure(error)

    // ATL-782: transient failures are QUEUED for durable retry (persisted outbox, replayed
    // on next launch/sale with backoff). NOT queued: deterministic defects (same payload
    // refused every attempt) and permanent refusals (409/422 — since #427 the idempotent
    // paths return SUCCESS on a benign re-send, so a surfaced conflict names a constraint
    // this payload hits deterministically; queueing it would only replay once and drop).
    // Not queued is not the same as not reported: ATL-1487 makes the refusal loud (`isRefused`).
    // ATL-985: WHICH ORG lost the write. Hoisted out of the enqueue branch below because it is
    // needed on every failure, queued or not — "does org X have a bleed" was unanswerable from
    // the error stream without it, and answering it by joining on user id does not work when the
    // org is the unit of the cutover decision.
    // ATL-1077: an explicit `orgFirestoreId` from the caller WINS over the session's org. It is
    // the more specific statement — the caller naming the entity's owner — and it is the only
    // thing that can be right when the session has no org yet (signup). Falls back to the
    // session for the ~99% of call sites that pass nothing, so no existing path changes.
    const org = getOrganization() as { id?: string } | null
    const orgFirestoreId =
      typeof context?.orgFirestoreId === 'string' && context.orgFirestoreId.length > 0
        ? context.orgFirestoreId
        : org?.id

    // ATL-1445 stage 3, P8 — dormant while no organization is Supabase-master (B1 emits it only for
    // one). A 409 `WRITE_MASTER_SUPABASE` naming this
    // organization means the backend no longer accepts Firestore-first mirrors from it: Supabase is the
    // master, and this request — byte-identical to the master write by the request-parity rule
    // (stage-3 §7) — is queued in the v1 outbox, whose drain converts it into outbox v2, instead of
    // being lost as a refusal (`global/Write/mirrorRefusalConversion.ts`: the drain is the one converter,
    // so it cannot overtake an older write of the same document). `liveMasterRefusalOf` is synchronous and null for every
    // other error, so for every response today's backend sends nothing here awaits, logs or reports,
    // and the rest of this block runs exactly as before.
    if (liveMasterRefusalOf(error) !== null) {
      const conversion = await convertLiveRefusedMirror(label, error, {
        orgFirestoreId,
        firestoreId: context?.firestoreId,
      })
      if (conversion === 'queued') {
        // Not a loss: the write is durable in the v1 outbox, and the drain kicked for it converts it into
        // outbox v2 as a master write (the drain is the one converter, so document order holds). The
        // conversion module has reported it; a refusal report here would count a write that is safe.
        // eslint-disable-next-line no-console
        console.log(
          `[dualWrite] ${label} refused as a Firestore-first mirror of a Supabase-master organization — queued for conversion into a master write`,
          JSON.stringify({ module, op, ...(orgFirestoreId ? { orgFirestoreId } : {}) }),
        )
        reportOutcome(onOutcome, label, { kind: 'converted-to-master-write' })
        return null
      }
      // `unresolved` / `not-applicable`: reported below exactly as today (loud: a refused write).
    }

    let queued = false
    if (!isDefect && !isPermanentReplayFailure(error)) {
      try {
        // The 3s race bounds a wedge in cross-tab locking (web): a frozen tab holding the
        // queue lock must degrade this to `queued:false` (loud tag), never hang checkout.
        // If the race times out but the enqueue later completes, the tag OVER-reports loss
        // (queued:false for a write that did persist) — deliberately the safe direction; do
        // not "fix" it by waiting.
        let raceTimer: ReturnType<typeof setTimeout> | undefined
        queued = await Promise.race([
          enqueueDualWriteFailure(label, error, {
            // The other half of the round-2 `fromReplay` fix: a replayed delete must not supersede
            // the document across all labels on the FAILURE path either (round-3 review finding 1).
            fromReplay: isPendingSkipReplay,
            firestoreId: context?.firestoreId,
            orgFirestoreId,
            // ATL-1207: the delete's precondition travels with it into the durable queue, which is
            // where it matters most — that queue holds an entry for seven days, and a recreate on
            // another till is far likelier over a week than over a session.
            deletedFrom: context?.deletedFrom,
          }),
          new Promise<false>((resolve) => {
            raceTimer = setTimeout(() => resolve(false), 3000)
          }),
        ]).finally(() => clearTimeout(raceTimer))
      } catch {
        // Failing to persist must not mask the original failure report below.
      }
    }

    const detail = {
      module,
      op,
      queued,
      ...(orgFirestoreId ? { orgFirestoreId } : {}),
      ...(isDeadBackend ? { deadBackend: true } : {}),
      ...(isForbidden ? { forbidden: true } : {}),
      ...(refusal ? { refused: true, refusalCode: refusal } : {}),
      ...(isMigrationPaused ? { migrationPaused: true } : {}),
      ...(isDefect ? { defect: true, fields } : {}),
      ...(context || {}),
      ...(apiDetail ? { api: apiDetail } : {}),
    }

    // Structured, parseable console line (Winston/log-shipping friendly).
    // eslint-disable-next-line no-console
    console.error(
      `[dualWrite] ${label} failed (non-fatal — Firestore already committed)`,
      JSON.stringify({ ...detail, error: errorMessage(error) }),
    )

    // ATL-1644: the failure class and the Sentry fingerprint come out of ONE precedence chain, so the
    // outcome reported to the caller always names the issue Sentry groups the failure under — two
    // copies of this chain could drift apart without any test noticing. Why each class groups the way
    // it does is documented at `fingerprint` below.
    const [failureClass, fingerprint]: [DualWriteFailureClass, string[]] = isDeadBackend
      ? ['dead-backend', ['supabase-dual-write-dead-backend']]
      : isRouteNotFound
        ? ['route-not-found', ['supabase-dual-write-route-not-found', module, op]]
        : isForbidden
          ? ['forbidden', ['supabase-dual-write-forbidden', module, op]]
          : refusal
            ? ['refused', ['supabase-dual-write-refused', module, op, refusal]]
            : isDefect
              ? ['defect', ['supabase-dual-write-defect', module, op, ...fields]]
              : isMigrationPaused
                ? // Class-wide, like the dead backend: a pause refuses every op of the org identically.
                  ['migration-paused', ['supabase-dual-write-migration-paused']]
                : ['transient', ['supabase-dual-write', module, op]]

    Sentry.captureException(error, {
      // Deterministic payload defects are real, permanent dual-write losses → `error` so they
      // leave the transient-noise warning budget and become alertable. Transient failures
      // (401 not-migrated, 5xx, network, timeout) stay `warning` ONLY because they are now
      // queued in the persisted outbox and replayed until they land (ATL-782) — before the
      // outbox existed this line claimed they "self-heal" via the reconciliation sweep, which
      // is PAUSED (ATL-707), i.e. they were permanent losses logged below alert threshold.
      // A transient failure that could NOT be queued (`dual_write_outbox_queued:False`) is
      // still a permanent loss and is the thing to alert on.
      level: isDefect || isDeadBackend || isForbidden || isRefused ? 'error' : 'warning',
      tags: {
        supabase_dual_write_error: true,
        // Distinguishes a genuine payload bug (fix the shape) from benign transient drift.
        dual_write_defect: isDefect,
        // ATL-902: the API host itself is gone (Railway-era bundle). Every write from this
        // device is one-sided until it takes an OTA. NOTE: Sentry stores boolean tag values
        // capitalised — query `dual_write_dead_backend:True`, never `:true`.
        dual_write_dead_backend: isDeadBackend,
        // ATL-1137: the backend evaluated this caller's role and refused. Retrying with the same
        // session cannot change that, so it is a human's problem — a role that should hold the
        // permission and does not, or an endpoint whose guard disagrees with its own write path
        // (ATL-1134 was one guard producing four such buckets). NOTE: Sentry stores boolean tag
        // values capitalised — query `dual_write_forbidden:True`, never `:true`.
        dual_write_forbidden: isForbidden,
        // ATL-1194: the request reached a server that has no handler for this path — a stale base
        // URL, a misrouting proxy, or an endpoint not deployed. Held by the drain rather than
        // retried on the attempt budget, so it must be countable. NOTE: Sentry stores boolean tag
        // values capitalised — query `dual_write_route_not_found:True`, never `:true`.
        dual_write_route_not_found: isRouteNotFound,
        // ATL-1487: the backend refused the write (409/422) — it did not land and is not queued.
        // `dual_write_refusal_code` names which refusal (`foreign_unique_conflict`,
        // `money_leg_buried`, …, or `uncoded`). NOTE: Sentry stores boolean tag values
        // capitalised — query `dual_write_refused:True`, never `:true`.
        dual_write_refused: isRefused,
        ...(refusal ? { dual_write_refusal_code: refusal } : {}),
        // ATL-1805: refused because the org is being re-migrated; queued and held, not lost. Only
        // present when true, so every other failure's tag set is unchanged.
        ...(isMigrationPaused ? { dual_write_migration_paused: true } : {}),
        // ATL-782: whether this failure is durably queued for replay (outbox) — if false on
        // a non-defect, the write is lost unless the sweep/re-migration picks it up.
        dual_write_outbox_queued: queued,
        dual_write: label,
        dual_write_module: module,
        dual_write_op: op,
        // ATL-985: the org that lost the write, and the dimension the transport-failure
        // sampling in `sentryFilters.js` keys on. Absent only pre-login, when no dual-write
        // can be attributed to an org anyway.
        ...(orgFirestoreId ? { dual_write_org: orgFirestoreId } : {}),
      },
      // Transient failures: one issue per operation ("failure rate by module"). Defects: additionally
      // split by failing field path so each distinct shape bug is its own issue and a fix shows up as
      // that fingerprint going quiet (instead of being masked by the shared op bucket).
      // Dead-backend failures group by CLASS, not by operation: every module.op on the device
      // fails identically, so per-op fingerprints scattered one incident across ~45 issues (the
      // ATL-902 spread) and made the population look like unrelated noise. One issue per
      // platform is what "how many devices are stranded" actually needs.
      // ATL-1137 groups 403s per OPERATION rather than class-wide (the dead-backend choice):
      // a dead host fails every endpoint identically, whereas which endpoint's authorization
      // disagrees with its write path IS the actionable dimension — ATL-1134's single guard bug
      // surfaced as four distinct `users.*` buckets, and fusing them would have hidden that.
      // Ordered before `isDefect` only for determinism; the two are mutually exclusive (a defect
      // is a 400 or a client-side Zod rejection, neither of which carries a 403).
      // ATL-1194 groups route 404s per OPERATION, unlike the dead backend and like a 403: a dead
      // host fails EVERY endpoint identically, whereas a route 404 is by definition about one
      // path — which endpoint is missing is the actionable dimension, and fusing them class-wide
      // would hide a single undeployed route inside a fleet-wide base-URL problem.
      // ATL-1487 groups refusals per OPERATION and per refusal CODE: `supplies.create
      // foreign_unique_conflict` and `sales.update money_leg_buried` have different owners and
      // different fixes. Keyed by the code, never the message, so rewording a message cannot split
      // an issue. Exclusive with `isDefect` (409/422 vs 400) and with the 403/404 classes above.
      fingerprint,
      contexts: { dual_write: detail },
    })
    // ATL-1487: Sentry alone is not enough for a permanent loss — its plan refuses every event once
    // the monthly allowance is spent (ATL-1354). Counted in PostHog regardless.
    if (isRefused) reportRefusedWrite(label, error, 'live')
    reportOutcome(onOutcome, label, { kind: 'failed', queued, failureClass })
    return null
  }
  // Outside the try on purpose: even though `reportOutcome` cannot throw, a report from inside the
  // try would sit where a throw is read as a failed request and queued for a replay of a write that
  // already landed.
  reportOutcome(onOutcome, label, { kind: 'landed' })
  return landedResult
}

/**
 * A DELETE dual-write: durable like every other write, and free when the row was never there.
 *
 * ATL-1188. Three delete paths in `firebase.ts` (`savedCarts.delete`, `transactions.delete`,
 * `complexSales.delete`) called the API bare and hand-rolled the failure handling:
 *
 *     try { await api.deleteX(id) } catch (e) {
 *       if (e?.response?.status !== 404 && !e?.message?.includes('404')) {
 *         Sentry.captureException(e, { tags: { supabase_dual_write_error: true, dual_write: 'x.delete' } })
 *       }
 *     }
 *
 * Every property `bestEffortDualWrite` exists to provide was therefore absent on exactly the
 * operation that can least afford it. A delete is the asymmetric one: a lost CREATE leaves
 * Supabase short a row, which the next write of that document or a re-migration repairs, whereas a
 * lost DELETE leaves Supabase holding a row the customer removed — and at the read cutover that
 * row comes BACK, into their books, with no trace of where it came from. What was missing:
 *
 *  • the OUTBOX. This wrapper is the only thing that enqueues (ATL-782), so a 5xx, a timeout or a
 *    dropped connection on these three was a permanent one-sided delete. Nothing stands behind it:
 *    the writing reconciliation sweep is paused (ATL-707) and would not be the answer if it ran.
 *  • the TAGS. `dual_write_module`/`dual_write_op` are the dashboard's axes, `dual_write_org` is
 *    how "does org X have a bleed" is answered at all (ATL-985), and `dual_write_outbox_queued` is
 *    the field that separates "will retry" from "lost forever" — the one worth an alert.
 *  • the CLASSIFICATION. No dead-backend, forbidden or defect bucket, so an ATL-902 stranded
 *    device and a role refusal both landed as an untagged exception grouped by stack frame.
 *  • the GATE. The bare call ignored `dual_write_enabled`, so the documented instant kill switch
 *    (ATL-468 rollback) did not stop these writes. Now it does.
 *
 * What the hand-rolled version got RIGHT, and what this helper therefore preserves: a 404 is
 * expected and must stay free. Rows created before the org started dual-writing were never
 * mirrored, so deleting one legitimately finds nothing. Swallowing it inside `fn` — rather than
 * letting it reach the wrapper's catch — is what keeps it out of both the outbox and Sentry, and
 * it resolves as a SUCCESS on purpose: `noteDualWriteSuccess` purges the document across all
 * labels, so an older queued create for the same id cannot replay later and resurrect it.
 *
 * The 404s that are NOT free are excluded by `isEntityAbsentFailure`, not by this function — see
 * its docblock for why a dead-backend 404 must re-throw.
 *
 * `firestoreId` is REQUIRED rather than optional — but not for the reason it is tempting to give,
 * and the tempting reason is false about this code. `deriveDocumentId` DOES have a fallback for
 * DELETE: it scans the URL's path segments and takes the last id-shaped one, and the three routes
 * this was written for (`/saved-carts/:id`, `/transactions/:id`, `/complex-sales/:id`; since
 * ATL-1195 the wrapper serves every `*.delete` label) carry a 20-character
 * Firestore id that `FIRESTORE_ID_SEGMENT` matches. So omitting the context would NOT make these
 * three unqueueable today. (The case where it genuinely would is `printChecks`, whose URL
 * `/settings/constants/printChecks` has no 15+ alphanumeric segment at all — see the comment at
 * its call site in firebase.ts. Do not generalise that special case into a universal, which an
 * earlier revision of this docblock did.)
 *
 * It is required because the fallback is a heuristic and the context is a statement: the caller
 * knows which document this delete is for, whereas the URL scan infers it from shape and silently
 * refuses anything short, legacy or differently keyed. It is also the supersede/purge key
 * (`noteDualWriteSuccess`), which must be the DOCUMENT's id even where an endpoint keys its URL on
 * something else.
 *
 * TWO LIMITS THIS INTRODUCES, recorded as decisions rather than left to be rediscovered. Both are
 * consequences of a delete now taking the shared path, and neither existed while these three sites
 * called the API bare:
 *
 *  1. A GATE-DENIED DELETE IS REPLAYED IN-SESSION — but only in-session (RESOLVED by ATL-1192;
 *     the limit that remains is narrower than the one recorded here originally). An UNINFORMED
 *     deny (`rc-stale` / `rc-unavailable`, the measured ATL-911 signature where the SDK reports a
 *     fresh fetch and reads empty) still skips the delete, and it is still reported loudly by
 *     `reportGateSkip`. It is now also held by `rememberUninformedSkip` — which admits delete-class
 *     ops that state a `firestoreId`, this wrapper's contract by construction — and replayed once
 *     the policy heals, at which point a failure reaches the durable outbox through the normal
 *     path. What is still lost: a session that DIES before the policy heals (the queue is
 *     in-memory by nature — a gate-denied write never ran, so there is no `error.requestConfig`
 *     to persist), and a delete whose document is re-created before it replays — by a later live
 *     write in this session, or (ATL-1207) on any other device, which the drain sees by re-reading
 *     the entity from Firestore. Both are dropped deliberately rather than allowed to destroy a
 *     live row.
 *
 *     A second-order effect worth naming, because it makes the org's state internally
 *     inconsistent rather than merely stale, for as long as the skip goes unreplayed: in
 *     `deleteComplexSale` the `auditLogApi.createAuditLog` call that follows is RAW and ungated,
 *     so on a gate-denied delete Supabase gains an `audit_log` row asserting the complex sale was
 *     deleted while the `complex_sales` row is still there. The replay closes the window rather
 *     than the gap: the two disagree until the policy heals.
 *
 *  2. THE REPLAY PATH SHARES THE 404 CONTRACT (RESOLVED by ATL-1193). A delete that failed at
 *     TRANSPORT (offline till) is queued, and if the document turns out to be pre-dual-write the
 *     replay gets the same entity-absent 404. The drain used to treat it as retryable: it spent
 *     the entry's attempts, dropped it as `attempts-exhausted` (a reported loss of nothing), and
 *     rule 2 parked the document's other entries behind it. The drain now consults
 *     `isEntityAbsentFailure` for `*.delete` labels and removes the entry as delivered, which is
 *     the conclusion drawn here about the identical error. A dead-host, route or envelope-less
 *     404 is still held or retried there, as it is re-thrown here.
 */
export async function bestEffortDualWriteDelete(
  label: string,
  del: () => Promise<unknown>,
  context: DualWriteContext & { firestoreId: string },
): Promise<void> {
  try {
    await bestEffortDualWrite(
      label,
      async () => {
      try {
        await del()
      } catch (error) {
        if (!isEntityAbsentFailure(error)) throw error
        // The row predates this org's dual-write and was never mirrored. Nothing to delete,
        // nothing lost, nothing to report — and treated as a success so the document's queued
        // entries are purged.
      }
      // No return value. Not because a second id would be worse — `noteDualWriteSuccess` purges
      // on the UNION of the context id and any response ids precisely so a queued write cannot
      // dodge the purge by being keyed on the other spelling, i.e. more ids are strictly safer.
      // It is simply that every DELETE this wrapper serves answers `{ message }` or `{ success }`
      // (re-checked for the ATL-1195 labels), so there is no id to pass. If this wrapper is ever pointed at a delete endpoint that DOES return one, pass
      // it through rather than dropping it.
        return undefined
      },
      context,
    )
  } catch (error) {
    // ATL-1188 review 2. `bestEffortDualWrite` guards only the caller's `fn`: everything before
    // its `try` (`await resolveGateDecision()`, which reaches the one unguarded `remoteConfig()`
    // accessor on the uninformed branch, and `reportGateSkip`) and everything in its `catch`
    // (`getOrganization()`, the enqueue race, the Sentry call — a catch cannot catch itself) can
    // throw. The three sites this wrapper replaced caught EVERYTHING, including a TypeError, and
    // that guarantee is not decoration on a delete: a throw here reaches a screen that has
    // already committed the Firestore delete, which skips its local state update and invites the
    // cashier to delete again. In `deleteComplexSalePayment` the second attempt re-applies
    // `fieldValue.increment(-amount)` to `dailyStats` — one deletion, two decrements of a money
    // statistic. That is exactly the "the obvious human response makes it worse" case this
    // module's own docblock gives as the reason destructive dual-writes are never fatal.
    reportDeleteWrapperThrow(label, error, context)
  }
}

/**
 * Report a throw that escaped `bestEffortDualWrite` itself (as opposed to a failed write, which
 * that helper reports). Its own class and fingerprint because it is a defect in the dual-write
 * PLUMBING — not the payload, not the network, not authorization — and it means the delete was
 * one-sided with no outbox entry behind it. Swallowing it silently would be the no-op this repo
 * forbids; the whole body is itself guarded, because the reporting path is one of the things that
 * can be broken and the guard must not become the new source of the exception.
 */
function reportDeleteWrapperThrow(
  label: string,
  error: unknown,
  context?: DualWriteContext,
): void {
  try {
    const { module, op } = splitLabel(label)
    // eslint-disable-next-line no-console
    console.error(
      `[dualWrite] ${label} wrapper THREW (non-fatal — Firestore already committed, ` +
        `but this delete is one-sided and was NOT queued)`,
      JSON.stringify({ module, op, ...(context || {}), error: errorMessage(error) }),
    )
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        supabase_dual_write_error: true,
        dual_write: label,
        dual_write_module: module,
        dual_write_op: op,
        // Distinguishes "the write failed" (every other event this module emits) from "the
        // machinery that reports failures failed", which no existing tag could express.
        dual_write_wrapper_threw: true,
        dual_write_outbox_queued: false,
      },
      fingerprint: ['supabase-dual-write-delete-wrapper-threw', module, op],
      contexts: { dual_write: { module, op, wrapperThrew: true, ...(context || {}) } },
    })
  } catch {
    // Nothing left to do: reporting the reporting failure would recurse. The delete is already
    // correct in Firestore, which is the store the customer reads.
  }
}
